<!--
# -----------------------------------------------------------------------------
# Component: PolicyRiskDetails.vue
# Author: PST
# Created Date: 02/April/2019
# Comments: Policy Risk -> Risk Details Tab
# -----------------------------------------------------------------------------
-->
<template>
  <div>
    <v-container fluid grid-list-lg>
      <v-layout row wrap>
        <v-flex xs12>
          <v-card>
            <v-toolbar dense color="grey lighten-5" flat>
              <v-icon>mdi-format-align-justify</v-icon>
              <v-toolbar-title class="toolbar-title-2">{{ labels.mainTitle }}</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn class="mx-2" fab dark small color="primary" v-on="on" @click.prevent="showPromptAddRisk" :disabled="disableMisc">
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Add Risk</span>
              </v-tooltip>
              <warning v-model="promptAddRisk" acceptLabel="Proceed" title="Select Risk Group" @ok="doAddRisk">
              <v-autocomplete
                :items="riskGrpNos"
                v-model="mainForm.selectedRiskGrp"
                :label="labels.riskGrpNo"
                item-text="label"
                item-value="value"
                data-vv-name="mainForm.selectedRiskGrp"
                :data-vv-as="labels.riskGrpNo"
                v-validate="'required'"
                :error-messages="errors.collect('mainForm.selectedRiskGrp')"
                clearable
                :loading="loading"
              ></v-autocomplete>
            </warning>
            </v-toolbar>
            <v-card-text>
              <v-container fluid grid-list-lg>
                <v-layout row wrap>
                  <v-flex xs12>
                    <v-container fluid grid-list-sm>
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-data-table :headers="headers" :items="riskList" class="elevation-1" :loading="loading">
                            <template v-slot:item="row">
                              <tr>
                                <td>{{ row.item.riskGrpNoRiskNo }}</td>
                                <td>{{ row.item.busCls }} - {{ row.item.busClsDesc }}</td>
                                <td>{{ row.item.siCurr }}</td>
                                <td>{{ row.item.siExRate }}</td>
                                <td>{{ row.item.comDateFormat }}</td>
                                <td>{{ row.item.expDateFormat }}</td>
                                <td>{{ row.item.delRisk }}</td>
                                <td class="table-action-header">
                                  <table-action
                                    :rowData="row"
                                    review
                                    @review="doSelectRisk(row.item)"
                                    :remove="!disableMisc && row.item.delRisk === 'NO'"
                                    @remove="doPromptRemoveRisk(row.item)"
                                    :undo="!disableMisc && row.item.delRisk === 'YES'"
                                    undoText="Undo Delete Risk"
                                    @undo="doPromptUndoDelRisk(row.item)"
                                  ></table-action>
                                </td>
                              </tr>
                            </template>
                          </v-data-table>
                          <confirm v-model="promptUndoRemoveRisk" @ok="doUndoDelRisk">{{ undoDeleteTxt }}</confirm>
                          <confirm v-model="promptRemoveRisk" @ok="doRemoveRisk">{{ deleteTxt }}</confirm>
                        </v-flex>
                        <v-flex xs12>
                          <v-card v-show="showExpandRisk">
                            <v-toolbar dense color="grey lighten-5" flat>
                              <v-icon>mdi-format-align-justify</v-icon>
                              <v-toolbar-title class="toolbar-title-3">{{ labels.miscRiskTitle }}</v-toolbar-title>
                              <v-spacer></v-spacer>
                              <v-tooltip top v-if="wrkmanComp">
                                <template v-slot:activator="{ on }">
                                  <v-btn class="mx-2" fab dark small color="teal" v-on="on" @click="doEmpyeeDetDialog">
                                    <v-icon dark>mdi-account-box</v-icon>
                                  </v-btn>
                                </template>
                                <span>Employee's Company Details</span>
                              </v-tooltip>
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-btn class="mx-2" fab dark small color="accent" v-on="on" @click="doRiskAccum">
                                    <v-icon dark>mdi-chart-areaspline</v-icon>
                                  </v-btn>
                                </template>
                                <span>Risk Accumulation</span>
                              </v-tooltip>
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-btn class="mx-2" fab dark small color="purple" v-on="on" @click.native="doRiskTextDialog">
                                    <v-icon dark>mdi-note-text</v-icon>
                                  </v-btn>
                                </template>
                                <span>Risk Text</span>
                              </v-tooltip>
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-btn class="mx-2" fab dark small color="indigo" v-on="on" @click="doRiskClauseList">
                                    <v-icon dark>mdi-library-books</v-icon>
                                  </v-btn>
                                </template>
                                <span>Risk Clause</span>
                              </v-tooltip>
                              <v-tooltip top>
                                <template v-slot:activator="{ on }">
                                  <v-btn class="mx-2" fab dark small color="pink" v-on="on" @click="doPolRiskStatsList">
                                    <v-icon dark>mdi-chart-bar</v-icon>
                                  </v-btn>
                                </template>
                                <span>Risk Statistics</span>
                              </v-tooltip>
                              <v-btn text icon @click="showExpandRisk = false">
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </v-toolbar>
                            <v-card-text>
                              <v-container fluid grid-list-lg>
                                <v-layout row wrap>
                                  <v-flex lg2 md2 xs12>
                                    <v-text-field
                                      v-model="mainForm.riskGrpNo"
                                      :label="labels.riskGrpNo"
                                      :error-messages="errors.collect('riskGrpNo')"
                                      data-vv-name="riskGrpNo"
                                      :data-vv-as="labels.riskGrpNo"
                                      :disabled="true"
                                    ></v-text-field>
                                  </v-flex>
                                  <v-flex lg2 md2 xs12>
                                    <v-text-field
                                      v-model="mainForm.riskNo"
                                      :label="labels.riskNo"
                                      :error-messages="errors.collect('riskNo')"
                                      data-vv-name="riskNo"
                                      :data-vv-as="labels.riskNo"
                                      :disabled="true"
                                    ></v-text-field>
                                  </v-flex>
                                  <v-flex lg2 md2 xs12>
                                    <v-checkbox v-model="mainForm.delRisk" :label="labels.delRisk" :disabled="true"></v-checkbox>
                                  </v-flex>
                                  <v-flex lg6 md6 xs12>
                                    <!-- <v-autocomplete :items="busClsList" v-model="mainForm.busCls" :label="labels.busCls" item-text="label" item-value="value" data-vv-name="busCls" :data-vv-as="labels.busCls" :error-messages="errors.collect('busCls')" v-validate="'required'" :loading="loading" @input="doBusClsInit" :disabled="disableMisc"></v-autocomplete> -->
                                    <v-select
                                      :items="busClsList"
                                        item-text="label"
                                        item-value="value"
                                      v-model="mainForm.busCls"
                                      :label="labels.busCls"
                                      :error-messages="errors.collect('busCls')"
                                      data-vv-name="busCls"
                                      :data-vv-as="labels.busCls"
                                      append-icon="mdi-magnify"
                                      @click:append="getHelpList('busCls')"
                                      v-validate="'required'"
                                      :loading="loading"
                                      @input="doBusClsInit"
                                      :disabled="disableMisc"
                                    />
                                  </v-flex>
                                  <v-flex lg3 md3 xs12>
                                    <cal-picker
                                      v-model="mainForm.comDate"
                                      :label="labels.comDate"
                                      v-validate="comDateValidation"
                                      :data-vv-as="labels.comDate"
                                      data-vv-name="comDate"
                                      :error-messages="errors.collect('comDate')"
                                      @input="doComDate"
                                      :loading="dateLoading"
                                      :disabled="disableMisc || busClsAppID !== 'MO'"
                                    ></cal-picker>
                                    <warning v-model="warnComDate" @ok="doAcceptComDate" @cancel="doCancelComDate">{{ warnComDateMsg }}</warning>
                                  </v-flex>
                                  <v-flex lg3 md3 xs12>
                                    <cal-picker
                                      v-model="mainForm.expDate"
                                      :label="labels.expDate"
                                      v-validate="expDateValidation"
                                      :data-vv-as="labels.expDate"
                                      data-vv-name="expDate"
                                      :error-messages="errors.collect('expDate')"
                                      :hint="daysCoverage"
                                      :loading="dateLoading"
                                      @input="doExpDate"
                                      :disabled="disableExpDate || busClsAppID !== 'MO'"
                                    ></cal-picker>
                                    <warning v-model="warnExpDate" @ok="doAcceptExpDate">{{ warnExpDateMsg }}</warning>
                                  </v-flex>
                                  <v-flex lg4 md3 xs12>
                                    <v-autocomplete
                                      :items="siCurrList"
                                      v-model="mainForm.siCurr"
                                      :label="labels.siCurr"
                                      item-text="label"
                                      item-value="value"
                                      data-vv-name="siCurr"
                                      :data-vv-as="labels.siCurr"
                                      :error-messages="errors.collect('siCurr')"
                                      v-validate="'required'"
                                      :loading="loading"
                                      @input="doSiCurr"
                                      :disabled="disableMisc"
                                      clearable
                                    ></v-autocomplete>
                                  </v-flex>
                                  <v-flex lg2 md3 xs12>
                                    <v-text-field
                                      v-model="mainForm.siExRate"
                                      :label="labels.siExRate"
                                      :error-messages="errors.collect('siExRate')"
                                      data-vv-name="siExRate"
                                      :data-vv-as="labels.siExRate"
                                      v-num-mask="{ negative: false, intScale: 18, decScale: 7 }"
                                      v-validate="'required|mask_decimal:7|mask_min_value:0.0000001|max:18'"
                                      :loading="loading"
                                      :disabled="disableMisc"
                                    ></v-text-field>
                                  </v-flex>
                                  <!-- <v-flex lg2 md2 xs12>
                                    <v-text-field readonly v-model="mainForm.indGrp" :label="labels.indGrp" :error-messages="errors.collect('indGrp')" data-vv-name="indGrp" :data-vv-as="labels.indGrp" append-icon="mdi-magnify" @click:append="getHelpList('perIndGrp')" @input="getIndGrp" :disabled="perIndGrp"></v-text-field>
                                  </v-flex>
                                  <v-flex lg4 md4 xs12>
                                    <v-text-field readonly v-model="indGrpDesc" :label="labels.indGrpDesc" :disabled="perIndGrp"></v-text-field>
                                  </v-flex> -->
                                  <v-flex lg6 md6 xs12>
                                    <v-autocomplete
                                      :items="stdRiskTxtList"
                                      v-model="mainForm.stdRiskTxtRef"
                                      :label="labels.stdRiskTxtRef"
                                      item-text="label"
                                      item-value="value"
                                      data-vv-name="stdRiskTxtRef"
                                      :data-vv-as="labels.stdRiskTxtRef"
                                      :error-messages="errors.collect('stdRiskTxtRef')"
                                      :loading="stdRiskTxtRefLoading"
                                      clearable
                                    ></v-autocomplete>
                                  </v-flex>
                                  <v-flex lg12 md12 xs12>
                                    <div v-if="busClsAppID === 'BD'">
                                      <PolicyBondRiskDetails
                                        ref="polBondRisk"
                                        :polRisk="mainForm"
                                        @hide-init-loading="hideInitLoading"
                                      ></PolicyBondRiskDetails>
                                    </div>
                                    <div v-if="busClsAppID === 'FI'">
                                      <PolicyFireRiskDetails
                                        ref="polFireRisk"
                                        :polRisk="mainForm"
                                        @hide-init-loading="hideInitLoading"
                                      ></PolicyFireRiskDetails>
                                    </div>
                                    <div v-if="busClsAppID === 'MO'">
                                      <PolicyMotorRiskDetails
                                        ref="polMtrRisk"
                                        :polRisk="mainForm"
                                        @hide-init-loading="hideInitLoading"
                                      ></PolicyMotorRiskDetails>
                                    </div>
                                    <div v-if="busClsAppID === 'HU'">
                                      <PolicyHullRiskDetails
                                        ref="polHullRisk"
                                        :polRisk="mainForm"
                                        @hide-init-loading="hideInitLoading"
                                      ></PolicyHullRiskDetails>
                                    </div>
                                    <div v-if="busClsAppID === 'CA'">
                                      <PolicyCargoRiskDetails
                                        ref="polCargoRisk"
                                        :polRisk="mainForm"
                                        @hide-init-loading="hideInitLoading"
                                      ></PolicyCargoRiskDetails>
                                    </div>
                                  </v-flex>
                                  <v-flex xs12>
                                    <v-card-actions>
                                      <v-spacer></v-spacer>
                                      <v-btn depressed @click="showExpandRisk = false"><span class="btn-normal-error">Close</span></v-btn>
                                      <v-btn depressed @click.prevent="doSave"><span class="btn-normal-primary">Save Risk</span></v-btn>
                                    </v-card-actions>
                                  </v-flex>
                                </v-layout>
                              </v-container>
                            </v-card-text>
                          </v-card>
                        </v-flex>
                        <v-flex xs12>
                          <v-btn class="mr-2" color="error" dark :loading="policy.loading" @click.prevent="doExit">
                            {{ labels.exit }}
                            <v-icon dark right>mdi-exit-to-app</v-icon>
                          </v-btn>
                          <v-btn class="mr-2" color="primary" dark :loading="policy.loading" @click.prevent="doProceed">
                            {{ labels.proceed }}
                            <v-icon dark right>mdi-arrow-right-bold-box</v-icon>
                          </v-btn>
                        </v-flex>
                      </v-layout>
                    </v-container>
                  </v-flex>
                </v-layout>
              </v-container>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog v-model="perIndGrpList" width="900px" persistent>
      <PerIndGrpHelpList
        v-if="perIndGrpHelpList"
        :indGrp="mainForm.indGrp"
        @close="doClose"
        @doAssign="doAssignIndustryHelpList($event)"
      ></PerIndGrpHelpList>
    </v-dialog>

    <v-dialog v-model="busClsHelpList" width="900px" persistent>
      <BusClsByPolTypeHelpList
        v-if="busClsHelpList"
        @close="doClose"
        :polType="polMaster.polType"
        @assign-busCls="doAssignBusClsHelpList($event)"
      ></BusClsByPolTypeHelpList>
    </v-dialog>

    <!-- Note: Moved all dialog boxes at the bottom -->
    <v-dialog v-model="riskAccumDialog" width="1200px" persistent>
      <v-container fluid grid-list-sm>
        <v-layout row wrap>
          <v-flex xs12>
            <v-card>
              <v-toolbar dense color="grey lighten-5" flat>
                <v-icon>mdi-format-align-justify</v-icon>
                <v-toolbar-title class="toolbar-title-2">{{ labels.riskBlock }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn color="primary" dark class="mb-2" @click="doAddRiskBlock">
                  New Risk Block
                  <v-icon right>mdi-plus</v-icon>
                </v-btn>
                <v-btn text icon @click="docloseRiskAccum">
                  <v-icon>mdi-close</v-icon>
                </v-btn>
              </v-toolbar>
              <v-card-text>
                <v-container fluid grid-list-sm>
                  <v-layout row wrap>
                    <v-flex xs12 class="mt-2">
                      <template>
                        <v-data-table
                          v-model="riskAccum.selected"
                          :headers="riskAccum.headers"
                          :items="riskAccum.items"
                          :loading="riskAccum.loading"
                          item-key="seqNo"
                          class="elevation-1"
                        >
                          <template v-slot:item="row">
                            <tr :active="row.selected" @click="row.selected = !row.selected">
                              <td>{{ row.item.seqNo }}</td>
                              <td>{{ row.item.blkGrp }}</td>
                              <td>{{ row.item.blkSGrp1 }}</td>
                              <td>{{ row.item.blkSGrp2 }}</td>
                              <td>{{ row.item.blk }}</td>
                              <td>{{ row.item.floatSilim }}</td>
                              <td>{{ row.item.floatPrem }}</td>
                              <td>{{ row.item.cfmRiskYN }}</td>
                              <td class="table-action-header">
                                <table-action
                                  :rowData="row"
                                  review
                                  @review="doSelectRiskBlock(row.item)"
                                  remove
                                  @remove="doPromptRemoveRiskBlock(row.item)"
                                ></table-action>
                              </td>
                            </tr>
                          </template>
                        </v-data-table>
                        <confirm v-model="promptRemoveRiskBlock" @ok="doRemoveRiskBlock">{{ deleteRiskBlock }}</confirm>
                      </template>
                    </v-flex>
                    <v-flex xs12 v-if="riskAccumForm.seqNo !== undefined">
                      <v-card>
                        <v-toolbar dense color="grey lighten-3" flat>
                          <v-icon>mdi-pencil</v-icon>
                          <v-toolbar-title class="toolbar-title-3">{{ labels.riskBlockDetails }}</v-toolbar-title>
                          <v-spacer></v-spacer>
                        </v-toolbar>
                        <v-card-text>
                          <v-container fluid grid-list-lg>
                            <v-layout row wrap>
                              <v-flex lg6 md6 xs12>
                                <v-text-field v-model="riskAccumForm.seqNo" :label="labels.seqNo" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex lg6 md6 xs12>
                                <v-checkbox v-model="riskAccumForm.cfmRisk" :label="labels.cfmRisk" :disabled="true"></v-checkbox>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field v-model="riskAccumForm.blkGrpLabel" :label="labels.blkGrp" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field
                                  :disabled="
                                    riskAccumForm.seqNo === undefined ||
                                      riskAccumForm.blkGrpLabel === undefined ||
                                      riskAccumForm.blkGrpLabel === null ||
                                      riskAccumForm.blkGrpLabel === ''
                                  "
                                  v-model="riskAccumForm.blkGrpLblS"
                                  :label="labels.blkGrpLblS"
                                  data-vv-name="blkGrpLblS"
                                  :data-vv-as="labels.blkGrpLblS"
                                  :error-messages="errors.collect('blkGrpLblS')"
                                  v-validate="valBlkGrp"
                                  @blur="getShowBlkGrp"
                                >
                                  <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                    <v-icon color="info" @click="doShowBlkGrp">mdi-magnify</v-icon>
                                  </v-slide-x-reverse-transition>
                                </v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-subheader class="queryLabel" v-show="riskAccumForm.blkGrpLblL != undefined">{{ labels.blkGrpLblL }} :</v-subheader>
                                <div class="queryValue" v-show="riskAccumForm.blkGrpLblL != undefined">{{ riskAccumForm.blkGrpLblL }}</div>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field v-model="riskAccumForm.blkSGrp1Label" :label="labels.blkSGrp1" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field
                                  :disabled="
                                    riskAccumForm.seqNo === undefined ||
                                      riskAccumForm.blkSGrp1Label === undefined ||
                                      riskAccumForm.blkSGrp1Label === null ||
                                      riskAccumForm.blkSGrp1Label === ''
                                  "
                                  v-model="riskAccumForm.blksGrp1LblS"
                                  :label="labels.blksGrp1LblS"
                                  data-vv-name="blksGrp1LblS"
                                  :data-vv-as="labels.blksGrp1LblS"
                                  :error-messages="errors.collect('blksGrp1LblS')"
                                  v-validate="valBlkSGrp1"
                                  @blur="getShowBlkSGrp1"
                                >
                                  <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                    <v-icon color="info" @click="doShowBlkSGrp1">mdi-magnify</v-icon>
                                  </v-slide-x-reverse-transition>
                                </v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-subheader class="queryLabel" v-show="riskAccumForm.blksGrp1LblL != undefined"
                                  >{{ labels.blksGrp1LblL }} :
                                </v-subheader>
                                <div class="queryValue" v-show="riskAccumForm.blksGrp1LblL != undefined">{{ riskAccumForm.blksGrp1LblL }}</div>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field v-model="riskAccumForm.blkSGrp2Label" :label="labels.blkSGrp2" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field
                                  :disabled="
                                    riskAccumForm.seqNo === undefined ||
                                      riskAccumForm.blkSGrp2Label === undefined ||
                                      riskAccumForm.blkSGrp2Label === null ||
                                      riskAccumForm.blkSGrp2Label === ''
                                  "
                                  v-model="riskAccumForm.blksGrp2LblS"
                                  :label="labels.blksGrp2LblS"
                                  data-vv-name="blksGrp2LblS"
                                  :data-vv-as="labels.blksGrp2LblS"
                                  :error-messages="errors.collect('blksGrp2LblS')"
                                  v-validate="valBlkSGrp2"
                                  @blur="getShowBlkSGrp2"
                                >
                                  <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                    <v-icon color="info" @click="doShowBlkSGrp2">mdi-magnify</v-icon>
                                  </v-slide-x-reverse-transition>
                                </v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-subheader class="queryLabel" v-show="riskAccumForm.blksGrp2LblL != undefined"
                                  >{{ labels.blksGrp2LblL }} :
                                </v-subheader>
                                <div class="queryValue" v-show="riskAccumForm.blksGrp2LblL != undefined">{{ riskAccumForm.blksGrp2LblL }}</div>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field v-model="riskAccumForm.blkLabel" :label="labels.blk" :disabled="true"></v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-text-field
                                  :disabled="
                                    riskAccumForm.seqNo === undefined ||
                                      riskAccumForm.blkLabel === undefined ||
                                      riskAccumForm.blkLabel === null ||
                                      riskAccumForm.blkLabel === ''
                                  "
                                  :label="labels.blksLblS"
                                  data-vv-name="blksLblS"
                                  v-model="riskAccumForm.blksLblS"
                                  :data-vv-as="labels.blksLblS"
                                  :error-messages="errors.collect('blksLblS')"
                                  v-validate="valBlk"
                                  @blur="getShowBlk"
                                >
                                  <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                                    <v-icon color="info" @click="doShowBlk">mdi-magnify</v-icon>
                                  </v-slide-x-reverse-transition>
                                </v-text-field>
                              </v-flex>
                              <v-flex lg4 md6 xs12>
                                <v-subheader class="queryLabel" v-show="riskAccumForm.blksLblL != undefined">{{ labels.blksLblL }} : </v-subheader>
                                <div class="queryValue" v-show="riskAccumForm.blksLblL != undefined">{{ riskAccumForm.blksLblL }}</div>
                              </v-flex>
                              <v-flex lg6 md6 xs12>
                                <v-text-field
                                  :disabled="riskAccumForm.seqNo === undefined"
                                  v-model="riskAccumForm.floatSilim"
                                  :label="labels.floatSilim"
                                  :error-messages="errors.collect('floatSilim')"
                                  data-vv-name="floatSilim"
                                  :data-vv-as="labels.floatSilim"
                                  v-num-mask="{ negative: false, intScale: 18, decScale: 2 }"
                                ></v-text-field>
                              </v-flex>
                              <v-flex lg6 md6 xs12>
                                <v-text-field
                                  :disabled="riskAccumForm.seqNo === undefined"
                                  v-model="riskAccumForm.floatPrem"
                                  :label="labels.floatPrem"
                                  :error-messages="errors.collect('floatPrem')"
                                  data-vv-name="floatPrem"
                                  :data-vv-as="labels.floatPrem"
                                  v-num-mask="{ negative: false, intScale: 18, decScale: 2 }"
                                ></v-text-field>
                              </v-flex>
                              <v-flex lg3 md3 xs6>
                                <v-subheader class="queryLabel">{{ labels.createdBy }}</v-subheader>
                                <v-subheader class="queryValue">{{ riskAccumForm.createdBy }} - {{ riskAccumForm.createdName }}</v-subheader>
                              </v-flex>
                              <v-flex lg3 md3 xs6>
                                <v-subheader class="queryLabel">{{ labels.createdDate }}</v-subheader>
                                <v-subheader class="queryValue">{{ riskAccumForm.createdDate }}</v-subheader>
                              </v-flex>
                              <v-flex lg3 md3 xs6>
                                <v-subheader class="queryLabel">{{ labels.modifiedBy }}</v-subheader>
                                <v-subheader class="queryValue">{{ riskAccumForm.modifiedBy }} - {{ riskAccumForm.modifiedName }}</v-subheader>
                              </v-flex>
                              <v-flex lg3 md3 xs6>
                                <v-subheader class="queryLabel">{{ labels.modifiedDate }}</v-subheader>
                                <v-subheader class="queryValue">{{ riskAccumForm.modifiedDate }}</v-subheader>
                              </v-flex>
                              <v-flex xs12>
                                <v-card-actions>
                                  <v-spacer></v-spacer>
                                  <v-btn depressed @click="docloseRiskAccum"><span class="btn-normal-error">Close</span></v-btn>
                                  <v-btn depressed @click="doSaveRiskAccum"><span class="btn-normal-primary">Save</span></v-btn>
                                </v-card-actions>
                              </v-flex>
                            </v-layout>
                          </v-container>
                        </v-card-text>
                      </v-card>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-dialog>

    <v-dialog v-model="riskTextDialog" width="50%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.riskText }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12>
                <v-tabs v-model="tabMenu" slider-color="teal">
                  <v-tab ripple :key="1">{{ labels.riskUppTxt }}</v-tab>
                  <v-tab ripple :key="2">{{ labels.riskLwrTxt }}</v-tab>
                </v-tabs>
                <v-tabs-items v-model="tabMenu">
                  <v-tab-item :key="1">
                    <v-card>
                      <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12>
                            <v-textarea
                              v-model="riskTextForm.riskUppTxt"
                              :error-messages="errors.collect('riskUppTxt')"
                              data-vv-validate-on="change"
                              data-vv-name="riskUppTxt"
                              :data-vv-as="labels.riskUppTxt"
                              :rows="10"
                            ></v-textarea>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                  <v-tab-item :key="2">
                    <v-card>
                      <v-card-text>
                        <v-layout row wrap>
                          <v-flex xs12>
                            <v-textarea
                              v-model="riskTextForm.riskLwrTxt"
                              :error-messages="errors.collect('riskLwrTxt')"
                              data-vv-validate-on="change"
                              data-vv-name="riskLwrTxt"
                              :data-vv-as="labels.riskLwrTxt"
                              :rows="10"
                            ></v-textarea>
                          </v-flex>
                        </v-layout>
                      </v-card-text>
                    </v-card>
                  </v-tab-item>
                </v-tabs-items>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="riskTextDialog = false"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click="doSaveRiskTxt"><span class="btn-normal-primary">Save</span></v-btn>
                  <v-btn depressed @click.native="loadPreSetDialog = true" @click="doLoadPreset"
                    ><span class="btn-normal-primary">{{ labels.preset }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadPreSetDialog" width="70%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.defineText }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex lg6 xs12>
                <v-autocomplete
                  :items="scnRefNoList"
                  v-model="scnRefNo"
                  :label="labels.scnRefNo"
                  item-text="label"
                  item-value="value"
                  data-vv-name="scnRefNo"
                  :data-vv-as="labels.scnRefNo"
                  :error-messages="errors.collect('scnRefNo')"
                  readonly
                ></v-autocomplete>
              </v-flex>
              <v-flex lg6 xs12> </v-flex>
              <v-flex lg6 xs12>
                <v-text-field v-model="polTypeDesc" :label="labels.polType" data-vv-name="polTypeDesc" readonly></v-text-field>
              </v-flex>
              <v-flex lg6 xs12> </v-flex>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    :headers="defaultHeaders"
                    :items="defaulText"
                    v-model="defTextSelected"
                    :loading="loadingTxt"
                    item-key="txtRef"
                    :options.sync="pagination"
                    show-select
                    class="elevation-1"
                  >
                    <template v-slot:items="row">
                      <tr>
                        <td>
                          <v-checkbox v-model="row.selected" primary hide-details :rowData="row"></v-checkbox>
                        </td>
                        <td>{{ row.item.txtRef }}</td>
                        <td>{{ row.item.txtTitle }}</td>
                        <td>{{ row.item.defTxt }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-flex xs12>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn depressed @click.native="loadPreSetDialog = false"><span class="btn-normal-error">Close</span></v-btn>
              <v-btn depressed @click="doSelectDefText"><span class="btn-normal-primary">Select</span></v-btn>
            </v-card-actions>
          </v-flex>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="empyeeDetDialog" width="65%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.empyeeDet }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="empyeeDetDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <form data-vv-scope="empyeeDetForm">
              <v-layout row wrap>
                <v-flex xs6 md4 lg4>
                  <v-subheader class="queryLabel">{{ labels.polNo }}</v-subheader>
                  <span class="queryValue" v-if="empyeeDetForm.polNo !== undefined">{{ empyeeDetForm.polNo }}</span>
                  <span class="queryValue" v-else>-</span>
                </v-flex>
                <v-flex xs6 md4 lg4>
                  <v-subheader class="queryLabel">{{ labels.renCntEndtCnt }}</v-subheader>
                  <span class="queryValue" v-if="empyeeDetForm.renCnt !== undefined && empyeeDetForm.endtCnt !== undefined"
                    >{{ empyeeDetForm.renCnt }} / {{ empyeeDetForm.endtCnt }}</span
                  >
                  <span class="queryValue" v-else>-</span>
                </v-flex>
                <v-flex xs6 md4 lg4>
                  <v-subheader class="queryLabel">{{ labels.riskGrpNoRiskNo }}</v-subheader>
                  <span class="queryValue" v-if="empyeeDetForm.riskGrpNo !== undefined && empyeeDetForm.riskNo !== undefined"
                    >{{ empyeeDetForm.riskGrpNo }} / {{ empyeeDetForm.riskNo }}</span
                  >
                  <span class="queryValue" v-else>-</span>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 md6 lg6>
                  <v-text-field
                    v-model="empyeeDetForm.empyeeCoUEN"
                    :label="labels.empyeeCoUEN"
                    :error-messages="errors.collect('empyeeDetForm.empyeeCoUEN')"
                    data-vv-name="empyeeDetForm.empyeeCoUEN"
                    :data-vv-as="labels.empyeeCoUEN"
                    v-validate="'max:15'"
                  ></v-text-field>
                  <!-- <warning v-model="warnEmpyeeCoUEN" @ok="doAcceptEmpyeeCoUEN">{{ warnEmpyeeCoUENMsg }}</warning> -->
                </v-flex>
                <v-flex xs12 md6 lg6>
                  <cal-picker
                    v-model="empyeeDetForm.dnldDate"
                    :label="labels.dnldDate"
                    :data-vv-as="labels.dnldDate"
                    data-vv-name="empyeeDetForm.dnldDate"
                    :error-messages="errors.collect('empyeeDetForm.dnldDate')"
                    :disabled="true"
                  ></cal-picker>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-text-field
                    v-model="empyeeDetForm.empyeeCoName"
                    :label="labels.empyeeCoName"
                    :error-messages="errors.collect('empyeeDetForm.empyeeCoName')"
                    data-vv-name="empyeeDetForm.empyeeCoName"
                    :data-vv-as="labels.empyeeCoName"
                  ></v-text-field>
                  <!-- <warning v-model="warnEmpyeeCoName" @ok="doAcceptEmpyeeCoName">{{ warnEmpyeeCoNameMsg }}</warning> -->
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                    v-model="empyeeDetForm.empyeeCoName2"
                    :label="labels.empyeeCoName2"
                    :error-messages="errors.collect('empyeeDetForm.empyeeCoName2')"
                    data-vv-name="empyeeDetForm.empyeeCoName2"
                    :data-vv-as="labels.empyeeCoName2"
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 md6 lg6>
                  <cal-picker
                    v-model="empyeeDetForm.cancelDate"
                    :label="labels.cancelDate"
                    :data-vv-as="labels.cancelDate"
                    data-vv-name="empyeeDetForm.cancelDate"
                    :error-messages="errors.collect('empyeeDetForm.cancelDate')"
                    v-validate="empyeeDetCancelDateValidation"
                  ></cal-picker>
                </v-flex>
                <v-flex xs12 md6 lg6>
                  <cal-picker
                    v-model="empyeeDetForm.suspendDate"
                    :label="labels.suspendDate"
                    :data-vv-as="labels.suspendDate"
                    data-vv-name="empyeeDetForm.suspendDate"
                    :error-messages="errors.collect('empyeeDetForm.suspendDate')"
                    v-validate="empyeeDetSuspendDateValidation"
                  ></cal-picker>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 md6 lg6>
                  <v-text-field
                    v-model="empyeeDetForm.noEmpyeeInsd"
                    :label="labels.noEmpyeeInsd"
                    :error-messages="errors.collect('empyeeDetForm.noEmpyeeInsd')"
                    data-vv-name="empyeeDetForm.noEmpyeeInsd"
                    :data-vv-as="labels.noEmpyeeInsd"
                  ></v-text-field>
                  <!-- <warning v-model="warnNoEmpyeeInsd" @ok="doAcceptNoEmpyeeInsd">{{ warnNoEmpyeeInsdMsg }}</warning> -->
                </v-flex>
                <v-flex xs12 md6 lg6>
                  <v-autocomplete
                    :items="termReasons"
                    v-model="empyeeDetForm.termCode"
                    :label="labels.termCode"
                    item-text="label"
                    item-value="value"
                    data-vv-name="empyeeDetForm.termCode"
                    :data-vv-as="labels.termCode"
                    :error-messages="errors.collect('empyeeDetForm.termCode')"
                    v-validate="termReasonValidation"
                    clearable
                  ></v-autocomplete>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex xs12 md6 lg6>
                  <v-autocomplete
                    :items="injuredTypes"
                    v-model="empyeeDetForm.injuredType"
                    :label="labels.injuredType"
                    item-text="label"
                    item-value="value"
                    data-vv-name="empyeeDetForm.injuredType"
                    :data-vv-as="labels.injuredType"
                    :error-messages="errors.collect('empyeeDetForm.injuredType')"
                    v-validate="injuredTypeValidation"
                    clearable
                  ></v-autocomplete>
                </v-flex>
              </v-layout>
            </form>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-btn color="error" @click="empyeeDetDialog = false" :loading="submitting">
            <span>Exit</span>
            <v-icon right dark>mdi-exit-to-app</v-icon>
          </v-btn>
          <v-btn color="primary" @click="doEmpyeeDet" :loading="submitting">
            <span>Save</span>
            <v-icon right dark>mdi-content-save</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskClauseDialog" width="50%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.riskClause }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="riskClse.selected"
                    :headers="riskClse.headers"
                    :items="riskClse.items"
                    :loading="riskClse.loading"
                    :options.sync="riskClse.pagination"
                    item-key="clauseNo"
                    class="elevation-1"
                    show-select
                  >
                    <!-- <template v-slot:item="row">
                      <tr :active="row.selected" @click="row.selected = !row.selected">
                        <td>{{ row.item.clauseNo }}</td>
                        <td>{{ row.item.clauseTitle }}</td>
                        <td>{{ row.item.clauseTxt }}</td>
                      </tr>
                    </template> -->
                  </v-data-table>
                </template>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="riskClauseDialog = false" @click.prevent="doResetRiskClse"
                    ><span class="btn-normal-error">Close</span></v-btn
                  >
                  <v-btn depressed @click.native="doPromptDelRiskClause"
                    ><span class="btn-normal-error">{{ labels.delete }}</span></v-btn
                  >
                  <v-btn depressed @click.prevent="doRiskClauseSave"><span class="btn-normal-primary">Save</span></v-btn>
                  <v-btn depressed @click.native="loadClausePreSetDialog = true"
                    ><span class="btn-normal-primary">{{ labels.preset }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadClausePreSetDialog" width="70%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.defineClause }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex lg4 xs6>
                <v-autocomplete
                  :items="clauseRefNoList"
                  v-model="riskClse.clauseRefNo"
                  :label="labels.clauseRefNo"
                  item-text="label"
                  item-value="value"
                  data-vv-name="clauseRefNo"
                  :data-vv-as="labels.clauseRefNo"
                  :error-messages="errors.collect('clauseRefNo')"
                  readonly
                ></v-autocomplete>
              </v-flex>
              <v-flex lg6 xs6> </v-flex>
              <v-flex lg4 xs6>
                <v-text-field v-model="polTypeDesc" :label="labels.polType" data-vv-name="polTypeDesc" readonly></v-text-field>
              </v-flex>
              <v-flex lg4 xs6>
                <v-text-field v-model="riskClse.clauseNo" :label="labels.clauseNo" data-vv-name="clauseNo"></v-text-field>
              </v-flex>
              <v-flex lg6 xs6> </v-flex>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="clause.selected"
                    :headers="clause.headers"
                    :items="clause.displayItems"
                    item-key="clauseNo"
                    :options.sync="clause.pagination"
                    show-select
                    class="elevation-1"
                  >
                  </v-data-table>
                </template>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="loadClausePreSetDialog = false"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click.native="loadClausePreSetDialog = false" @click="doSelectClause"
                    ><span class="btn-normal-primary">{{ labels.select }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="loadDelRiskClseDialog" width="50%" persistent :disabled="doCheckDelRiskClseItem">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.delRiskClses }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex lg10 xs12>
                <div>{{ labels.delRiskClseMsg }}</div>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn depressed @click.native="loadDelRiskClseDialog = false" @click="doResetRiskClseSeletedColumn"
            ><span class="btn-normal-error">{{ labels.no }}</span></v-btn
          >
          <v-btn depressed @click="doRiskClauseDelete"
            ><span class="btn-normal-primary">{{ labels.yes }}</span></v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskStatsDialog" width="50%" persistent>
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">{{ labels.riskStats }}</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="polRiskStats.selected"
                    :headers="polRiskStats.headers"
                    :items="polRiskStats.items"
                    item-key="statsType"
                    class="elevation-1"
                    :loading="polRiskStats.loading"
                    :options.sync="polRiskStats.pagination"
                  >
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.statsType }}</td>
                        <td>{{ row.item.statsTypeDesc }}</td>
                        <td>{{ row.item.statsVal }}</td>
                        <td>
                          <v-slide-x-reverse-transition mode="out-in" slot="append-outer">
                            <v-icon color="info" @click="doShowStatValue(row.item)">mdi-magnify</v-icon>
                          </v-slide-x-reverse-transition>
                          <!-- <v-icon color="info" @click="doShowStatValue(row.item)" v-text="'mdi-magnify'"></v-icon> -->
                        </td>
                        <td>{{ row.item.statsValDesc }}</td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-flex>
              <v-flex xs12>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn depressed @click.native="riskStatsDialog = false"><span class="btn-normal-error">Close</span></v-btn>
                  <v-btn depressed @click="doRiskStatsSave"
                    ><span class="btn-normal-primary">{{ labels.save }}</span></v-btn
                  >
                </v-card-actions>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statsValDialog" width="60%" persistent>
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Statistics Value
          <v-spacer></v-spacer>
          <v-btn text icon @click="statsValDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-container fluid grid-list-sm>
            <v-layout row wrap>
              <v-flex xs12 class="mt-2">
                <template>
                  <v-data-table
                    v-model="statsVal.selected"
                    :headers="statsVal.headers"
                    :items="statsVal.items"
                    item-key="statsVal"
                    class="elevation-1"
                    :loading="statsVal.loading"
                  >
                    <template v-slot:item="row">
                      <tr>
                        <td>{{ row.item.statsVal }}</td>
                        <td>{{ row.item.statsValDesc }}</td>
                        <td style="text-align: center">
                          <v-btn icon color="primary" text type="submit" @click.prevent="doSelectedStatsValue(row.item)">
                            <v-icon>mdi-content-copy</v-icon>
                          </v-btn>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </template>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskAccumBlkGrpDialog" width="1280px">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">Select Block Group</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="doClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-flex xs12>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div><v-icon>mdi-filter-variant</v-icon> Filters</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkGrp"
                              :label="labels.blkGrp"
                              :error-messages="errors.collect('blkGrp')"
                              data-vv-name="blkGrp"
                              :data-vv-as="labels.blkGrp"
                            ></v-text-field>
                          </v-flex>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkGrpDescS"
                              :label="labels.blkGrpDescS"
                              :error-messages="errors.collect('blkGrpDescS')"
                              data-vv-name="blkGrpDescS"
                              :data-vv-as="labels.blkGrpDescS"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="red darken-1" text @click.native="doClearFilters('blkGrp')">Clear Filters</v-btn>
                      <v-btn color="primary" text type="submit" @click.prevent="doFilters('blkGrp')">Apply Filters</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-flex>
          <v-spacer class="mt-1"></v-spacer>
          <v-flex xs12 class="mt-2">
            <v-data-table
              class="elevation-1"
              v-bind:headers="listBlkGrp"
              v-bind:items="blkGrpItems"
              :server-items-length="totalItemsBlkGrp"
              :footer-props="{ 'items-per-page-options': itemsPerPage }"
              v-bind:options.sync="paginationBlkGrp"
              item-key="x"
              :loading="loading"
              :no-data-text="noDataText"
            >
              <template v-slot:item="row">
                <tr>
                  <td>{{ row.item.blkGrp }}</td>
                  <td>{{ row.item.blkGrpDescS }}</td>
                  <td class="table-action-header">
                    <table-action :rowData="row" copy @copy="doSelectBlkGrp(row.item)"></table-action>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskAccumBlkSubGrp1Dialog" width="1280px">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">Select Block Sub Group 1</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="doClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-flex xs12>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div><v-icon>mdi-filter-variant</v-icon> Filters</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkSubGrp1"
                              :label="labels.blkSGrp1"
                              :error-messages="errors.collect('blkSubGrp1')"
                              data-vv-name="blkSubGrp1"
                              :data-vv-as="labels.blkSubGrp1"
                            ></v-text-field>
                          </v-flex>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkSubGrp1DescS"
                              :label="labels.blkSubGrp1DescS"
                              :error-messages="errors.collect('blkSubGrp1DescS')"
                              data-vv-name="blkSubGrp1DescS"
                              :data-vv-as="labels.blkSubGrp1DescS"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="red darken-1" text @click.native="doClearFilters('blkSubGrp1')">Clear Filters</v-btn>
                      <v-btn color="primary" text type="submit" @click.prevent="doFilters('blkSubGrp1')">Apply Filters</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-flex>
          <v-spacer class="mt-1"></v-spacer>
          <v-flex xs12 class="mt-2">
            <v-data-table
              class="elevation-1"
              v-bind:headers="listBlkSubGrp1"
              v-bind:items="blkSubGrp1Items"
              :server-items-length="totalItemsBlkSubGrp1"
              :footer-props="{ 'items-per-page-options': itemsPerPage }"
              v-bind:options.sync="paginationBlkSubGrp1"
              item-key="x"
              :loading="loading"
              :no-data-text="noDataText"
            >
              <template v-slot:item="row">
                <tr>
                  <td>{{ row.item.blkSubGrp1 }}</td>
                  <td>{{ row.item.blkSubGrp1DescS }}</td>
                  <td class="table-action-header">
                    <table-action :rowData="row" copy @copy="doSelectBlkSubGrp1(row.item)"></table-action>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskAccumBlkSubGrp2Dialog" width="1280px">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">Select Block Sub Group 2</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="doClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-flex xs12>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div><v-icon>mdi-filter-variant</v-icon> Filters</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkSubGrp2"
                              :label="labels.blkSGrp2"
                              :error-messages="errors.collect('blkSubGrp2')"
                              data-vv-name="blkSubGrp2"
                              :data-vv-as="labels.blkSubGrp2"
                            ></v-text-field>
                          </v-flex>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkSubGrp2DescS"
                              :label="labels.blkSubGrp2DescS"
                              :error-messages="errors.collect('blkSubGrp2DescS')"
                              data-vv-name="blkSubGrp2DescS"
                              :data-vv-as="labels.blkSubGrp2DescS"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="red darken-1" text @click.native="doClearFilters('blkSubGrp2')">Clear Filters</v-btn>
                      <v-btn color="primary" text type="submit" @click.prevent="doFilters('blkSubGrp2')">Apply Filters</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-flex>
          <v-spacer class="mt-1"></v-spacer>
          <v-flex xs12 class="mt-2">
            <v-data-table
              class="elevation-1"
              v-bind:headers="listBlkSubGrp2"
              v-bind:items="blkSubGrp2Items"
              :server-items-length="totalItemsBlkSubGrp2"
              :footer-props="{ 'items-per-page-options': itemsPerPage }"
              v-bind:options.sync="paginationBlkSubGrp2"
              item-key="x"
              :loading="loading"
              :no-data-text="noDataText"
            >
              <template v-slot:item="row">
                <tr>
                  <td>{{ row.item.blkSubGrp2 }}</td>
                  <td>{{ row.item.blkSubGrp2DescS }}</td>
                  <td class="table-action-header">
                    <table-action :rowData="row" copy @copy="doSelectBlkSubGrp2(row.item)"></table-action>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="riskAccumBlkDialog" width="1280px">
      <v-card>
        <v-toolbar dense color="grey lighten-5" flat>
          <v-icon>mdi-format-align-justify</v-icon>
          <v-toolbar-title class="toolbar-title-2">Select Block</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn text icon @click="doClose">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-flex xs12>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <div><v-icon>mdi-filter-variant</v-icon> Filters</div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-card>
                    <v-card-text>
                      <v-container fluid grid-list-lg>
                        <v-layout row wrap>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blk"
                              :label="labels.blk"
                              :error-messages="errors.collect('blk')"
                              data-vv-name="blk"
                              :data-vv-as="labels.blk"
                            ></v-text-field>
                          </v-flex>
                          <v-flex md6>
                            <v-text-field
                              v-model="filters.blkDescS"
                              :label="labels.blkDescS"
                              :error-messages="errors.collect('blkDescS')"
                              data-vv-name="blkDescS"
                              :data-vv-as="labels.blkDescS"
                            ></v-text-field>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="red darken-1" text @click.native="doClearFilters('blk')">Clear Filters</v-btn>
                      <v-btn color="primary" text type="submit" @click.prevent="doFilters('blk')">Apply Filters</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-flex>
          <v-spacer class="mt-1"></v-spacer>
          <v-flex xs12 class="mt-2">
            <v-data-table
              class="elevation-1"
              v-bind:headers="listBlk"
              v-bind:items="blkItems"
              :server-items-length="totalItemsBlk"
              :footer-props="{ 'items-per-page-options': itemsPerPage }"
              v-bind:options.sync="paginationBlk"
              item-key="x"
              :loading="loading"
              :no-data-text="noDataText"
            >
              <template v-slot:item="row">
                <tr>
                  <td>{{ row.item.blk }}</td>
                  <td>{{ row.item.blkGrp }}</td>
                  <td>{{ row.item.blkDesc }}</td>
                  <td class="table-action-header">
                    <table-action :rowData="row" copy @copy="doSelectBlk(row.item)"></table-action>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="policy.loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-3">
          Processing...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!--AR: Added for sub risk details loading-->
    <v-dialog v-model="policy.initLoading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pa-3">
          Processing...
          <v-progress-linear indeterminate color="white" class="mb-0"></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showErrorDialog" width="800px">
      <v-card>
        <v-toolbar dense color="grey lighten-3" flat>
          <v-icon>mdi-information</v-icon>
          <v-toolbar-title>System Information</v-toolbar-title>
          <v-spacer></v-spacer>
        </v-toolbar>
        <v-card-text>
          <div v-if="messages.length > 0" class="pb-3">
            <span class="info-dialog-msg-header">Oops something went wrong...</span>
            <v-spacer class="mt-2"></v-spacer>
            <ul class="showErrorMsgs">
              <li v-for="(error, index) in messages" :key="index">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-1" text @click.native="showErrorDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <confirm v-model="warnRiskTab" @ok="doSave"> Any unsaved work will be lost. Choose "Accept" to save your work. </confirm>

    <warning v-model="warnEmpyeeDet" @ok="doAcceptEmpyeeDet">{{ warnEmpyeeDetMsg }}</warning>
    <warning v-model="warnEmpyeeCoUEN" @ok="doAcceptEmpyeeCoUEN">{{ warnEmpyeeCoUENMsg }}</warning>
    <warning v-model="warnEmpyeeCoName" @ok="doAcceptEmpyeeCoName">{{ warnEmpyeeCoNameMsg }}</warning>
    <warning v-model="warnNoEmpyeeInsd" @ok="doAcceptNoEmpyeeInsd">{{ warnNoEmpyeeInsdMsg }}</warning>
    <warning v-model="warnBondNo" @ok="doProceedBondNo">{{ warnBondNoMsg }}</warning>

    <!-- <warning v-model="warnBondNo" @ok="doAcceptWarnBondNo">{{ warnBondNoMsg }}</warning> -->
  </div>
</template>

<script>
import MessageMixin from '@/mixins/message'
import { toDate } from 'date-fns'
import { formatYesNo } from '@/utils/boolean'
import { formatDisplayDate, formatDisplayDateTime2 } from '@/utils/date'
import { expandURI } from '@/utils/ajax'
import subDays from 'date-fns/subDays'
import addMonths from 'date-fns/addMonths'
import addYears from 'date-fns/addYears'
import differenceInDays from 'date-fns/differenceInDays'
import PolicyFireRiskDetails from './PolicyFireRiskDetails'
import PolicyBondRiskDetails from './PolicyBondRiskDetails'
import PolicyMotorRiskDetails from './PolicyMotorRiskDetails'
import PolicyHullRiskDetails from './PolicyHullRiskDetails'
import PolicyCargoRiskDetails from './PolicyCargoRiskDetails'
// import { POLICY_RISK_TAB, POLICY_SELECTED_RISKNO, STORE_STANDARD_RISK_TEXT } from '@/store/types'
import { POLICY_RISK_TAB, POLICY_SELECTED_RISKNO } from '@/store/types'
import { riskAccumListMixin } from '@/mixins/riskAccumulation/riskAccumListMixin'
import { riskAccumAjaxMixin } from '@/mixins/riskAccumulation/riskAccumAjaxMixin'
import { formatPrice } from '@/utils/index'
import { removeFormatNumber } from '@/utils/number'
import PerIndGrpHelpList from './helplist/PerIndGrpHelpList'
import * as TableConst from '@/mixins/table'
import BusClsByPolTypeHelpList from '@/components/core/common/dialog/BusClsByPolTypeHelpList'
import clone from 'clone'
import { isEqual } from '@/utils/compare'

export default {
  data() {
    return {
      promptAddRisk: false,
      warnRiskTab: false,
      showExpandRisk: true,
      showExpand1: true,
      showErrorDialog: false,
      labels: {
        mainTitle: 'Policy Risk',
        tableTitle: 'Risk List',
        addRisk: 'Add Risk',
        delRisk: 'Delete Risk',
        editRisk: 'Edit Risk',
        miscRiskTitle: 'Miscellaneous Risk Details',
        riskAccum: 'Risk Accumulation',
        riskText: 'Risk Text',
        riskUppTxt: 'Upper Text',
        riskLwrTxt: 'Lower Text',
        riskClause: 'Risk Clause',
        riskStats: 'Risk Statistics',
        riskGrpNo: 'Group No.',
        riskNo: 'Risk No.',
        empyeeDet: "Employee's Company Details",
        empyeeCoUEN: 'Company UEN',
        empyeeCoName: 'Company Name',
        empyeeCoName2: 'Company Name 2',
        dnldDate: 'Download MOM Date',
        cancelDate: 'Cancel Date',
        suspendDate: 'Suspend Date',
        noEmpyeeInsd: 'Total No. of Workers',
        busCls: 'Business Class',
        comDate: 'Commencement Date',
        expDate: 'Expiry Date',
        siCurr: 'SI Currency',
        siExRate: 'SI Currency Rate',
        indGrp: 'Industry Group',
        indGrpDesc: 'Industry Group Description',
        collateralNo: 'Collateral Ref No',
        stdRiskTxtRef: 'Standard Text',
        bondRiskDetTitle: 'Bond Risk Details',
        marineCargoRiskDetTitle: 'Marine Cargo Risk Details',
        marineHullRiskDetTitle: 'Marine Hull Risk Details',
        medicalRiskDetTitle: 'Medical Risk Details',
        motorRiskDetTitle: 'Motor Risk Details',
        personalAccRiskDetTitle: 'Personal Accident Risk Details',
        exit: 'Exit',
        reset: 'Reset',
        save: 'Save',
        proceed: 'Proceed',
        preset: 'Load Preset',
        mailAddr: 'Same as Mailing Address',
        BlkHouseNo: 'Block / House No',
        unit: 'Unit No',
        interest: 'Interest',
        streetName: 'Street Name',
        buildingName: 'Building Name',
        state: 'State',
        country: 'Country',
        postcode: 'PostCode',
        occup: 'Occupancy',
        occupCls: 'Occupation Class',
        districtCls: 'District Class',
        constructionCls: 'Construction Class',
        occupationList: 'Occupation List',
        loading: 'Loading %',
        roofCls: 'Roof Class',
        heightOfBul: 'Height of Building',
        noOfStoreys: 'No of Storeys',
        latitudeX: 'Latitude X',
        latitudeY: 'Latitude Y',
        surveyRepNo: 'Survey Report No',
        surveyDate: 'Survey Date',
        defineText: 'Predefined Text',
        select: 'Select',
        scnRefNo: 'Screen Reference No',
        polTypeDesc: 'Policy Type',
        delete: 'Delete',
        delRiskClses: 'Delete Risk Clauses',
        delRiskClseMsg: 'Are you sure you want to delete risk clauses?',
        defineClause: 'Predefined Clause',
        clauseRefNo: 'Clause Ref No.',
        yes: 'Yes',
        no: 'No',
        editRiskBlock: 'Edit Risk Block',
        delRiskBlock: 'Delete Risk Block',
        riskBlock: 'Risk Block',
        riskBlockDetails: 'Risk Block Details',
        seqNo: 'Sequence No.',
        cfmRisk: 'Confirm Risk',
        blkGrp: 'Block Group',
        blkGrpDescS: 'Block Group Description',
        blkSGrp1: 'Block Sub Group 1',
        blkSGrp2: 'Block Sub Group 2',
        blk: 'Block',
        floatSilim: 'Floating SI',
        floatPrem: 'Floating Premium',
        createdBy: 'Created By',
        createdDate: 'Created Date',
        modifiedBy: 'Modified By',
        modifiedDate: 'Modified Date',
        blkSubGrp1DescS: 'Block Sub Group 1 Description',
        blkSubGrp2DescS: 'Block Sub Group 2 Description',
        blkDescS: 'Block Description',
        polNo: 'Policy No.',
        renCntEndtCnt: 'Ren / EndtCnt',
        riskGrpNoRiskNo: 'Risk Grp / No.',
        clauseNo: 'Clause No.',
        termCode: 'Termination Reason',
        injuredType: 'Injured Person Type'
      },
      mainForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        delRisk: undefined,
        busCls: undefined,
        comDate: undefined,
        expDate: undefined,
        siCurr: undefined,
        siExRate: undefined,
        indGrp: undefined,
        stdRiskTxtRef: undefined,
        formType: 'miscForm',
        updColl: undefined,
        applyGST: undefined,
        selectedRiskGrp: undefined
      },
      resetForm: {
        riskGrpNo: undefined,
        riskNo: undefined,
        delRisk: undefined,
        busCls: undefined,
        comDate: undefined,
        expDate: undefined,
        siCurr: undefined,
        siExRate: undefined,
        indGrp: undefined,
        stdRiskTxtRef: undefined,
        applyGST: undefined
      },
      riskTextForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        riskUppTxt: undefined,
        riskLwrTxt: undefined,
        formType: 'riskTextForm'
      },
      resetRiskTextForm: {
        riskUppTxt: undefined,
        riskLwrTxt: undefined
      },
      riskClseForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        clauses: []
      },
      empyeeDetForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        empyeeCoUEN: '',
        dnldDate: '',
        empyeeCoName: '',
        empyeeCoName2: '',
        cancelDate: '',
        suspendDate: '',
        noEmpyeeInsd: 0,
        termCode: '',
        injuredType: ''
      },
      headers: [
        { text: 'Risk No.', align: 'left', value: 'riskGrpNoRiskNo' },
        { text: 'Class', value: 'busCls' },
        { text: 'SI Currency', value: 'siCurr' },
        { text: 'SI Currency Rate', value: 'siExRate' },
        { text: 'Com Date', value: 'comDateFormat' },
        { text: 'Exp Date', value: 'expDateFormat' },
        { text: 'Del Risk', value: 'delRisk' },
        { text: 'Action', value: 'action', align: 'center' }
      ],
      riskList: [],
      loading: false,
      policy: {
        loading: false,
        saving: false,
        initLoading: false
      },
      riskAccumDialog: false,
      riskTextDialog: false,
      riskClauseDialog: false,
      riskStatsDialog: false,
      empyeeDetDialog: false,
      submitting: false,
      busClsList: [],
      siCurrList: [],
      industryList: [],
      stdRiskTxtList: [],
      polType: {
        comDateOpt: undefined,
        perBancBill: undefined,
        expiryTime: undefined
      },
      warnComDate: false,
      acceptComDate: false,
      warnComDateMsg: undefined,
      dateLoading: false,
      warnExpDate: false,
      acceptExpDate: false,
      warnExpDateMsg: undefined,
      stdRiskTxtRefLoading: false,
      disableMisc: undefined,
      tabMenu: undefined,
      loadPreSetDialog: undefined,
      scnRefNoList: [
        { label: 'Upper Text', value: '20' },
        { label: 'Lower text', value: '21' }
      ],
      scnRefNo: undefined,
      polTypeDesc: undefined,
      defaultHeaders: [
        { text: 'Text Ref No.', align: 'left', value: 'txtRef' },
        { text: 'Text Title', value: 'txtTitle' },
        { text: 'Default Text', value: 'defTxt' }
      ],
      defaulText: [],
      defTextSelected: [],
      loadingTxt: undefined,
      pagination: undefined,
      selectedRiskUppTxt: [],
      selectedRiskLwrTxt: [],
      itemsRiskUppTxt: [],
      itemsRiskLwrTxt: [],
      termReasons: [],
      injuredTypes: [],
      riskGrpNos: [],
      riskClse: {
        selected: [],
        headers: [
          { text: 'Clauses', align: 'left', value: 'clauseNo' },
          { text: 'Clauses Title', value: 'clauseTitle' },
          { text: 'Clauses Text', value: 'clauseTxt' }
        ],
        items: [],
        pagination: {
          itemsPerPage: 5
        },
        clauseRefNo: '02',
        loading: undefined,
        saving: undefined
      },
      riskAccum: {
        selected: [],
        headers: [
          { text: 'Seq.No.', value: 'seqNo' },
          { text: 'Block Group', value: 'blkGrp' },
          { text: 'Sub Group 1', value: 'blkSGrp1' },
          { text: 'Sub Group 2', value: 'blkSGrp2' },
          { text: 'Block', value: 'blk' },
          { text: 'Float SI', value: 'floatSilim' },
          { text: 'Float Prem', value: 'floatPrem' },
          { text: 'Confirm', value: 'cfmRisk' },
          { text: 'Actions', value: 'action', align: 'center' }
        ],
        items: [],
        loading: undefined,
        saving: undefined
      },
      loadDelRiskClseDialog: false,
      loadClausePreSetDialog: false,
      clauseRefNoList: [
        { label: 'Policy', value: '01' },
        { label: 'Risk', value: '02' },
        { label: 'Item', value: '03' },
        { label: 'Scheme', value: '04' }
      ],
      clause: {
        selected: [],
        headers: [
          { text: 'Clause No', align: 'left', value: 'clauseNo' },
          { text: 'Clause Title', value: 'clauseTitle' },
          { text: 'Clause Text', value: 'clauseTxt' },
          { text: 'Premium Warranty', value: 'premWar' }
        ],
        items: [],
        pagination: {
          itemsPerPage: 5
        },
        selectedClause: [],
        displayItems: []
      },
      polRiskStats: {
        selected: [],
        headers: [
          { text: 'Statistics Type', align: 'left', value: 'statsType' },
          { text: 'Description', value: 'statsTypeDesc' },
          { text: 'Statistics Value', value: '`statsVal`' },
          { text: '', sortable: false, value: 'searchStatsValue' },
          { text: 'Description', value: 'statsValDesc' }
        ],
        items: [],
        pagination: {
          itemsPerPage: -1
        },
        loading: undefined,
        saving: undefined
      },
      statsVal: {
        selected: [],
        headers: [
          { text: 'Statistics Value', align: 'left', value: 'statsVal' },
          { text: 'Description', value: 'statsValDesc' },
          { text: 'Action', align: 'center', sortable: false, value: '' }
        ],
        items: [],
        loading: undefined
      },
      statsValDialog: false,
      polRiskStatsForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        statsType: [],
        statsVal: []
      },
      selectedRisk: undefined,
      deleteTxt: undefined,
      promptRemoveRisk: undefined,
      busClsAppID: undefined,
      wrkmanComp: false,
      accumType: undefined,
      riskAccumForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined,
        seqNo: undefined,
        cfmRisk: undefined,
        blkGrpLabel: undefined,
        blkGrp: undefined,
        blkSGrp1Label: undefined,
        blkSGrp1: undefined,
        blkSGrp2Label: undefined,
        blkSGrp2: undefined,
        blkLabel: undefined,
        blk: undefined,
        floatSilim: undefined,
        floatPrem: undefined,
        blkGrpLblS: undefined,
        blkGrpLblL: undefined,
        createdName: undefined,
        modifiedName: undefined,
        createdBy: undefined,
        createdDate: undefined,
        modifiedBy: undefined,
        modifiedDate: undefined,
        blksGrp1LblS: undefined,
        blksGrp1LblL: undefined,
        blksGrp2LblS: undefined,
        blksGrp2LblL: undefined,
        blksLblS: undefined,
        blksLblL: undefined,
        effDate: undefined,
        addFld: undefined
      },
      riskAccumBlkGrpDialog: false,
      riskAccumBlkSubGrp1Dialog: false,
      riskAccumBlkSubGrp2Dialog: false,
      riskAccumBlkDialog: false,
      selectedRiskBlock: undefined,
      deleteRiskBlock: undefined,
      promptRemoveRiskBlock: undefined,
      perIndGrp: true,
      perIndGrpHelpList: false,
      perIndGrpList: false,
      indGrpDesc: undefined,
      saveMRisk: true,
      filters: {
        blkGrp: undefined,
        blkGrpDescS: undefined,
        blkSubGrp1: undefined,
        blkSubGrp1DescS: undefined,
        blkSubGrp2: undefined,
        blkSubGrp2DescS: undefined,
        blk: undefined,
        blkDescS: undefined
      },
      toogleArray: [false],
      valBlkGrp: 'required',
      valBlkSGrp1: 'required',
      valBlkSGrp2: 'required',
      valBlk: 'required',
      undoDeleteTxt: undefined,
      promptUndoRemoveRisk: undefined,
      policyRiskForm: {
        polNo: undefined,
        renCnt: undefined,
        endtCnt: undefined,
        riskGrpNo: undefined,
        riskNo: undefined
      },
      warnEmpyeeDetMsg: undefined,
      warnEmpyeeDet: false,
      acceptEmpyeeDet: false,
      warnEmpyeeCoUENMsg: undefined,
      warnEmpyeeCoUEN: false,
      acceptEmpyeeCoUEN: false,
      warnEmpyeeCoNameMsg: undefined,
      warnEmpyeeCoName: false,
      acceptEmpyeeCoName: false,
      warnNoEmpyeeInsdMsg: undefined,
      warnNoEmpyeeInsd: false,
      acceptNoEmpyeeInsd: false,
      busClsHelpList: false,
      warnBondNo: false,
      warnBondNoMsg: undefined,
      acceptBondNo: false,
      empyeeDetStat: {
        isBlank: false,
        isValid: true
      },
      empyeeDetExist: false
    }
  },
  components: {
    PolicyFireRiskDetails,
    PolicyBondRiskDetails,
    PolicyMotorRiskDetails,
    PolicyHullRiskDetails,
    PolicyCargoRiskDetails,
    PerIndGrpHelpList,
    BusClsByPolTypeHelpList
  },
  watch: {
    displayErrorDialog: {
      handler: function(messages) {
        if (messages && messages.length > 0) {
          this.showErrorDialog = true
        }
      },
      deep: true
    },
    'mainForm.riskNo': function(value) {
      this.$store.commit(POLICY_SELECTED_RISKNO, parseInt(value))
    },
    'riskClse.clauseNo': function(value) {
      if (value) {
        this.clause.displayItems = this.clause.items.filter(item => item.clauseNo.toLowerCase().includes(value.toLowerCase()))
      } else {
        this.clause.displayItems = this.clause.items
      }
    }
  },
  computed: {
    displayErrorDialog() {
      return this.messages
    },
    policyInfo() {
      return this.$store.state.underwriting.policy.policyInfo
    },
    polMaster() {
      return this.$store.state.underwriting.policy.polMaster
    },
    polRisk() {
      return this.$store.state.underwriting.policy.polRisk
    },
    comDateValidation() {
      if (this.polType.comDateOpt === 'M') {
        return 'required|start_enddate:' + this.polMaster.comDate
      } else if (this.polType.comDateOpt === 'N') {
        return 'required_blank'
      } else {
        return ''
      }
    },
    expDateValidation() {
      if (this.mainForm.comDate === undefined || this.mainForm.comDate === '' || this.mainForm.comDate === null) {
        return 'required_blank'
      } else if (this.mainForm.comDate !== undefined && this.mainForm.comDate !== '') {
        return 'required|start_enddate:' + this.mainForm.comDate + '|after_to_date:' + this.polMaster.expDate
      } else {
        return ''
      }
    },
    empyeeDetCancelDateValidation() {
      if (
        this.mainForm.delRisk &&
        (this.empyeeDetForm.cancelDate === undefined || this.empyeeDetForm.cancelDate === '' || this.empyeeDetForm.cancelDate === null)
      ) {
        return 'required|valField:' + 'The Cancel Date field is required.'
      } else if (
        !this.mainForm.delRisk &&
        (this.empyeeDetForm.cancelDate !== undefined || this.empyeeDetForm.cancelDate !== '' || this.empyeeDetForm.cancelDate !== null)
      ) {
        return 'valField:' + 'The Cancel Date field must be blank.'
      } else {
        return ''
      }
    },
    empyeeDetSuspendDateValidation() {
      if (
        (this.empyeeDetForm.cancelDate === undefined || this.empyeeDetForm.cancelDate === '' || this.empyeeDetForm.cancelDate === null) &&
        (this.empyeeDetForm.suspendDate !== undefined || this.empyeeDetForm.suspendDate !== '' || this.empyeeDetForm.suspendDate !== null)
      ) {
        return ''
      } else {
        return 'valField:' + 'Not permit to enter suspend date if cancel date is not blank.'
      }
    },
    empyeeDetNoEmpyeeInsdValidation() {
      if (
        parseInt(this.empyeeDetForm.noEmpyeeInsd) === 0 ||
        this.empyeeDetForm.noEmpyeeInsd === undefined ||
        this.empyeeDetForm.noEmpyeeInsd === '' ||
        this.empyeeDetForm.noEmpyeeInsd === null
      ) {
        return 'required|numeric|mask_min_value:1|valField:' + 'The Total No. of Workers field is required.'
      } else {
        return 'numeric|mask_min_value:1'
      }
    },
    termReasonValidation() {
      if (this.polMaster.polStat === 'CA') {
        if (this.empyeeDetForm.termCode === '' || this.empyeeDetForm.termCode === undefined || this.empyeeDetForm.termCode === null) {
          return 'required'
        } else {
          return null
        }
      } else {
        if (this.empyeeDetForm.termCode !== '' && this.empyeeDetForm.termCode !== undefined && this.empyeeDetForm.termCode !== null) {
          return 'valField:' + this.labels.termCode + ' must be blank'
        } else {
          return null
        }
      }
    },
    injuredTypeValidation() {
      if (this.empyeeDetForm.injuredType === '' || this.empyeeDetForm.injuredType === undefined || this.empyeeDetForm.injuredType === null) {
        return 'required'
      } else {
        return null
      }
    },
    daysCoverage() {
      if (this.mainForm.comDate && this.mainForm.expDate) {
        if (this.mainForm.expDate !== undefined && this.mainForm.comDate !== undefined) {
          return 'Days Covered: ' + (differenceInDays(toDate(new Date(this.mainForm.expDate)), toDate(new Date(this.mainForm.comDate))) + 1)
        } else {
          return undefined
        }
      } else {
        return undefined
      }
    },
    getSiCurr() {
      return this.siCurrList.filter(siCurr => {
        return siCurr.value === this.mainForm.siCurr
      })
    },
    getFirstRiskList() {
      return this.riskList.filter(risk => {
        return risk.riskGrpNoRiskNo === this.riskList[0].riskGrpNoRiskNo
      })
    },
    getNewRisk() {
      return this.riskList.filter(risk => {
        return risk.newRiskNo === true
      })
    },
    disableExpDate() {
      if (this.disableMisc === true) {
        return true
      } else if (this.polType.perBancBill === true) {
        return true
      } else {
        return false
      }
    },
    doCheckDelRiskClseItem() {
      return !this.riskClse.selected.length > 0
    },
    getNewRiskBlock() {
      return this.riskAccum.items.filter(item => {
        return item.newRec === true
      })
    },
    getIndGrpDesc() {
      return this.industryList.filter(indGrp => {
        return indGrp.value === this.mainForm.indGrp
      })
    }
  },
  methods: {
    async doClearFilters(value) {
      this.resetMessages()
      if (value === 'blkGrp') {
        this.filters.blkGrp = undefined
        this.filters.blkGrpDescS = undefined
        this.paginationBlkGrp.page = 1
        this.riskAccumBlkGrpInit(false)
      } else if (value === 'blkSubGrp1') {
        this.filters.blkSubGrp1 = undefined
        this.filters.blkSubGrp1DescS = undefined
        this.paginationBlkSubGrp1.page = 1
        this.riskAccumBlkSubGrp1Init(false)
      } else if (value === 'blkSubGrp2') {
        this.filters.blkSubGrp2 = undefined
        this.filters.blkSubGrp2DescS = undefined
        this.paginationBlkSubGrp2.page = 1
        this.riskAccumBlkSubGrp2Init(false)
      } else if (value === 'blk') {
        this.filters.blk = undefined
        this.filters.blkDescS = undefined
        this.paginationBlk.page = 1
        this.riskAccumBlkInit(false)
      }
    },
    async doFilters(value) {
      this.resetMessages()
      if (value === 'blkGrp') {
        this.paginationBlkGrp.page = 1
        this.riskAccumBlkGrpInit(false)
      } else if (value === 'blkSubGrp1') {
        this.paginationBlkSubGrp1.page = 1
        this.riskAccumBlkSubGrp1Init(false)
      } else if (value === 'blkSubGrp2') {
        this.paginationBlkSubGrp2.page = 1
        this.riskAccumBlkSubGrp2Init(false)
      } else if (value === 'blk') {
        this.paginationBlk.page = 1
        this.riskAccumBlkInit(false)
      }
    },
    async doInit() {
      this.policy.initLoading = true
      this.getPolType()
      this.getRiskGrpList()
      this.mainForm.polNo = this.polMaster.polNo
      this.mainForm.renCnt = this.polMaster.renCnt
      this.mainForm.endtCnt = this.polMaster.endtCnt
      this.polTypeDesc = this.policyInfo.polTypeDesc
      this.$set(this, 'riskList', [])
      await this.getMiscRiskParams()
      this.getRiskList().then(() => {
        if (this.getFirstRiskList.length > 0) {
          this.mainForm.riskGrpNo = this.getFirstRiskList[0].riskGrpNo
          this.mainForm.riskNo = this.getFirstRiskList[0].riskNo
          if (this.getFirstRiskList[0].delRisk === 'YES') {
            this.mainForm.delRisk = true
          } else {
            this.mainForm.delRisk = false
          }
          this.mainForm.busCls = this.getFirstRiskList[0].busCls
          this.mainForm.comDate = this.getFirstRiskList[0].comDate != null ? this.getFirstRiskList[0].comDate : undefined
          this.mainForm.expDate = this.getFirstRiskList[0].expDate != null ? this.getFirstRiskList[0].expDate : undefined
          this.mainForm.siCurr = this.getFirstRiskList[0].siCurr
          this.mainForm.siExRate = this.getFirstRiskList[0].siExRate
          this.mainForm.indGrp = this.getFirstRiskList[0].indGrp
          this.doIndGrpDesc()
          this.mainForm.stdRiskTxtRef = this.getFirstRiskList[0].stdRiskTxtRef
          this.mainForm.applyGST = this.getFirstRiskList[0].applyGST

          this.riskTextForm.riskUppTxt = this.getFirstRiskList[0].riskUppTxt
          this.riskTextForm.riskLwrTxt = this.getFirstRiskList[0].riskLwrTxt

          this.disableMisc = this.getFirstRiskList[0].canFindPkgPlan

          this.doBusCls()
          this.getRiskTextList()
          this.getClauseList()
          this.getTermReasonList()
          this.getInjuredTypeList()

          this.resetForm.riskGrpNo = this.mainForm.riskGrpNo
          this.resetForm.riskNo = this.mainForm.riskNo
          this.resetForm.delRisk = this.mainForm.delRisk
          this.resetForm.busCls = this.mainForm.busCls
          this.resetForm.comDate = this.mainForm.comDate
          this.resetForm.expDate = this.mainForm.expDate
          this.resetForm.siCurr = this.mainForm.siCurr
          this.resetForm.siExRate = this.mainForm.siExRate
          this.resetForm.indGrp = this.mainForm.indGrp
          // this.resetForm.stdRiskTxtRef = this.mainForm.stdRiskTxtRef
          this.resetForm.applyGST = this.mainForm.applyGST

          this.resetRiskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt
          this.resetRiskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt

          this.doSelectRisk(this.getFirstRiskList[0])
        }
      })
      this.copymainForm = clone(this.mainForm)
      await this.$store.dispatch('riskAccumulationInit')
      await this.$store.dispatch('riskAccumulationListInit')
    },
    doAcceptWarnBondNo() {
      this.acceptBondNo = true
    },
    async getRiskList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.riskList
      if (apiUrl) {
        try {
          this.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            this.riskList = []
            data.data.map(risk => {
              const convertRisk = {
                riskGrpNoRiskNo: risk.riskGrpNo.toString().padStart(2, '0') + '/' + risk.riskNo.toString().padStart(3, '0'),
                riskGrpNo: risk.riskGrpNo.toString().padStart(2, '0'),
                riskNo: risk.riskNo.toString().padStart(3, '0'),
                busCls: risk.busCls,
                busClsDesc: risk.busClsDesc,
                siCurr: risk.siCurr,
                siExRate: risk.siExRate.toFixed(7),
                comDate: risk.comDate,
                comDateFormat: formatDisplayDate(risk.comDate),
                expDate: risk.expDate,
                expDateFormat: formatDisplayDate(risk.expDate),
                delRisk: formatYesNo(risk.delRisk),
                indGrp: risk.indGrp,
                stdRiskTxtRef: risk.stdRiskTxtRef,
                riskUppTxt: risk.riskUppTxt,
                riskLwrTxt: risk.riskLwrTxt,
                canFindPkgPlan: risk.canFindPkgPlan,
                newRiskNo: risk.newRiskNo,
                applyGST: risk.applyGST
              }
              this.riskList.push(convertRisk)
            })
            this.loading = false
          } else {
            this.riskList = []
            this.loading = false
          }
        } catch (err) {
          this.policy.initLoading = false
          this.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getRiskList'))
      }
    },
    async getMiscRiskParams() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.miscRiskParams
      if (apiUrl) {
        try {
          const expand = {
            polType: this.polMaster.polType,
            effDate: new Date(this.polMaster.tranDate)
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          if (result.data.busClsList) {
            this.$set(this, 'busClsList', result.data.busClsList)
          }

          if (result.data.siCurrList) {
            this.$set(this, 'siCurrList', result.data.siCurrList)
          }

          if (result.data.industryList) {
            this.$set(this, 'industryList', result.data.industryList)
          }
        } catch (err) {
          this.policy.initLoading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getMiscRiskParams'))
      }
    },
    async getPolType() {
      const apiUrl = this.$store.state.underwriting.policyGeneralSettings.menu.polType
      if (apiUrl) {
        try {
          this.dateLoading = true
          const expand = {
            polType: this.polMaster.polType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data) {
            const { data } = result
            this.polType.expiryTime = data.expiryTime
            this.polType.comDateOpt = data.comDateOpt
            this.polType.perBancBill = data.perBancBill

            this.dateLoading = false
          }
        } catch (err) {
          this.policy.initLoading = false
          this.dateLoading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getPolType'))
      }
    },
    doComDate() {
      this.doWarnComDate()
      this.getDefExpDate()
    },
    doWarnComDate() {
      if (!this.acceptComDate) {
        var nodays = differenceInDays(toDate(new Date()), toDate(new Date(this.mainForm.comDate))) + 1
        if (nodays < -60) {
          this.warnComDateMsg = '10005 – This is more than 60 days later than Today. (10005)'
          this.warnComDate = true
          return false
        } else if (nodays > 60) {
          this.warnComDateMsg = '10004 – This is more than 60 days earlier than Today. (10004)'
          this.warnComDate = true
          return false
        }
      }
      return true
    },
    doAcceptComDate() {
      this.acceptComDate = true
    },
    async doWarnEmptyEmpyeeDet() {
      if (this.empyeeDetStat.isBlank === true) {
        if (!this.acceptEmpyeeDet) {
          this.warnEmpyeeDetMsg = 'No employee details.'
          this.warnEmpyeeDet = true
          return false
        }
      } else {
        if (this.empyeeDetStat.isValid === false) {
          this.warnEmpyeeDetMsg = 'Termination Reason cannot be blank.'
          this.warnEmpyeeDet = true
          this.empyeeDetDialog = true
          return false
        } else {
          this.empyeeDetExist = true
          return true
        }
      }
    },
    doAcceptEmpyeeDet() {
      if (this.empyeeDetStat.isBlank === true) {
        this.acceptEmpyeeDet = true
        this.doProceed()
      }
    },
    doWarnEmpyeeCoUEN() {
      if (this.wrkmanComp) {
        if (this.empyeeDetForm.empyeeCoUEN === null || this.empyeeDetForm.empyeeCoUEN === undefined || this.empyeeDetForm.empyeeCoUEN === '') {
          this.warnEmpyeeCoUENMsg = 'The Company UEN field is required.'
          this.warnEmpyeeCoUEN = true
          return false
        }
      }
      return true
    },
    doAcceptEmpyeeCoUEN() {
      this.acceptEmpyeeCoUEN = true
    },
    doWarnEmpyeeCoName() {
      if (this.wrkmanComp) {
        if (this.empyeeDetForm.empyeeCoName === null || this.empyeeDetForm.empyeeCoName === undefined || this.empyeeDetForm.empyeeCoName === '') {
          this.warnEmpyeeCoNameMsg = 'The Company Name field is required.'
          this.warnEmpyeeCoName = true
          return false
        }
      }
      return true
    },
    doAcceptEmpyeeCoName() {
      this.acceptEmpyeeCoName = true
    },
    doWarnNoEmpyeeInsd() {
      if (this.wrkmanComp) {
        if (parseInt(this.empyeeDetForm.noEmpyeeInsd) === 0) {
          this.warnNoEmpyeeInsdMsg = 'The Total No. of Workers field must be 1 or more.'
          this.warnNoEmpyeeInsd = true
          return false
        }
        if (this.empyeeDetForm.noEmpyeeInsd === undefined || this.empyeeDetForm.noEmpyeeInsd === '' || this.empyeeDetForm.noEmpyeeInsd === null) {
          this.warnNoEmpyeeInsdMsg = 'The Total No. of Workers field is required.'
          this.warnNoEmpyeeInsd = true
          return false
        }
      }
      return true
    },
    doAcceptNoEmpyeeInsd() {
      this.acceptNoEmpyeeInsd = true
    },
    getDefExpDate() {
      if (this.mainForm.comDate) {
        if (this.polMaster.billType === 'A') {
          // Add 1 year to end Date
          let expDate = addYears(this.mainForm.comDate, 1)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.mainForm.expDate = expDate
        } else if (this.polMaster.billType === 'M') {
          let expDate = addMonths(this.mainForm.comDate, 1)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.mainForm.expDate = expDate
        } else if (this.polMaster.billType === 'T') {
          let expDate = addMonths(this.mainForm.comDate, 4)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.mainForm.expDate = expDate
        } else if (this.polMaster.billType === 'Q') {
          let expDate = addMonths(this.mainForm.comDate, 3)
          if (this.polType.expiryTime === 2400) {
            expDate = subDays(expDate, 1)
          }
          this.mainForm.expDate = expDate
        }
      }
    },
    doCancelComDate() {
      this.mainForm.comDate = undefined
      this.doComDate()
    },
    doExpDate() {
      this.doWarnExpDate()
    },
    doWarnExpDate() {
      if (
        !this.acceptExpDate &&
        (this.mainForm.expDate === undefined || this.mainForm.expDate === '') &&
        this.mainForm.comDate !== undefined &&
        this.mainForm.comDate !== ''
      ) {
        this.warnExpDateMsg = '10006 – Expiry Date cannot be blank when Commencement Date entered. (10006)'
        this.warnExpDate = true
        return false
      }
      return true
    },
    doAcceptExpDate() {
      this.acceptExpDate = true
    },
    doSaveMiscRisk() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          this.policy.loading = true
          this.doMiscRiskSave().then(success => {
            if (success) {
              this.displayRiskList()
              this.$toast.success('Miscellaneous Risk Details Successfully Saved')
              this.policy.loading = false
            } else {
              this.$toast.error(this.failedMessage('miscellaneous risk'))
              this.policy.loading = false
            }
          })
        } else {
          this.policy.loading = false
          this.promptFieldCheck()
        }
      })
    },
    async doMiscRiskSave() {
      try {
        if (isEqual(this.mainForm, this.copymainForm) === true) {
          return true
        } else {
          const result = await this.$store.dispatch('savePolRisk', this.mainForm)
          if (result) {
            return true
          } else {
            return false
          }
        }
      } catch (err) {
        return false
      }
    },
    displayRiskList() {
      this.riskList = []
      if (this.polRisk.riskList) {
        this.polRisk.riskList.map(risk => {
          const convertRisk = {
            riskGrpNoRiskNo: risk.riskGrpNo.toString().padStart(2, '0') + '/' + risk.riskNo.toString().padStart(3, '0'),
            riskGrpNo: risk.riskGrpNo.toString().padStart(2, '0'),
            riskNo: risk.riskNo.toString().padStart(3, '0'),
            busCls: risk.busCls,
            busClsDesc: risk.busClsDesc,
            siCurr: risk.siCurr,
            siExRate: risk.siExRate.toFixed(7),
            comDate: risk.comDate,
            comDateFormat: risk.comDate != null ? formatDisplayDate(risk.comDate) : undefined,
            expDate: risk.expDate,
            expDateFormat: risk.expDate != null ? formatDisplayDate(risk.expDate) : undefined,
            delRisk: formatYesNo(risk.delRisk),
            indGrp: risk.indGrp,
            stdRiskTxtRef: risk.stdRiskTxtRef,
            riskUppTxt: risk.riskUppTxt,
            riskLwrTxt: risk.riskLwrTxt,
            canFindPkgPlan: risk.canFindPkgPlan,
            newRiskNo: risk.newRiskNo,
            applyGST: risk.applyGST
          }
          this.riskList.push(convertRisk)
        })
      } else {
        this.riskList = []
      }
    },
    doResetMiscRisk() {
      this.mainForm.riskGrpNo = this.resetForm.riskGrpNo
      this.mainForm.riskNo = this.resetForm.riskNo
      this.mainForm.delRisk = this.resetForm.delRisk
      this.mainForm.busCls = this.resetForm.busCls
      this.mainForm.comDate = this.resetForm.comDate
      this.mainForm.expDate = this.resetForm.expDate
      this.mainForm.siCurr = this.resetForm.siCurr
      this.mainForm.siExRate = this.resetForm.siExRate
      this.mainForm.indGrp = this.resetForm.indGrp
      // this.mainForm.stdRiskTxtRef = this.resetForm.stdRiskTxtRef
      this.mainForm.applyGST = this.resetForm.applyGST
      this.doIndGrpDesc()
    },
    async doValidateAllWarn() {
      let status = true
      if (!this.acceptComDate) {
        if (!this.doWarnComDate()) {
          status = false
        }
      }
      if (!this.acceptExpDate) {
        if (!this.doWarnExpDate()) {
          status = false
        }
      }
      if (this.busClsAppID === 'MO') {
        if (this.$refs.polMtrRisk) {
          if (!this.$refs.polMtrRisk.acceptVehRegNo || !this.$refs.polMtrRisk.acceptSuspectVeh) {
            if (!this.$refs.polMtrRisk.doWarnVehRegNo()) {
              status = false
            }
          }
          if (!this.$refs.polMtrRisk.acceptChasNo) {
            if (!this.$refs.polMtrRisk.doWarnChasNo()) {
              status = false
            }
          }
          // 2021-01-20 LYT OS-643
          if (!this.$refs.polMtrRisk.acceptEngCcTons) {
            if (!this.$refs.polMtrRisk.doWarnEngCcTons()) {
              status = false
            }
          }
        }
      }
      if (!this.acceptEmpyeeCoUEN) {
        if (!this.doWarnEmpyeeCoUEN()) {
          status = false
        }
      }
      if (!this.acceptEmpyeeCoName) {
        if (!this.doWarnEmpyeeCoName()) {
          status = false
        }
      }
      if (!this.acceptNoEmpyeeInsd) {
        if (!this.doWarnNoEmpyeeInsd()) {
          status = false
        }
      }
      return status
      // return true
    },
    doSiCurr() {
      if (this.getSiCurr.length > 0) {
        this.mainForm.siExRate = this.getSiCurr[0].secondaryValue.toFixed(7)
      }
    },
    doBusClsInit() {
      this.doBusCls()
      this.doInitSubComponent()
    },
    async doBusCls() {
      if (this.mainForm.busCls) {
        await this.getBusClsParam()
      }
    },
    async getBusClsParam() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.busClsParam
      if (apiUrl) {
        try {
          this.stdRiskTxtRefLoading = true
          const expand = {
            busCls: this.mainForm.busCls
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          this.$set(this, 'stdRiskTxtList', result.data.busClsStdTxtList)
          if (result.data) {
            this.busClsAppID = result.data.busClsAppID
            this.wrkmanComp = result.data.wrkmanComp
            this.mainForm.updColl = result.data.updColl
            this.copymainForm.updColl = result.data.updColl
            this.accumType = result.data.accumType
            this.riskAccumForm.blkLabel = result.data.blkLblS
            this.riskAccumForm.blkGrpLabel = result.data.blkGrpLblS
            this.riskAccumForm.blkSGrp1Label = result.data.blkSGrp1LblS
            this.riskAccumForm.blkSGrp2Label = result.data.blkSGrp2LblS
            if (result.data.perIndGrp) {
              this.perIndGrp = false
            } else {
              this.perIndGrp = true
            }
            // if (this.wrkmanComp) {
            //   this.getPolEmpyeeDet()
            // }
            this.mainForm.applyGST = result.data.applyGST
            if (this.resetForm.busCls !== this.mainForm.busCls) {
              if (this.stdRiskTxtList.length > 0) {
                this.mainForm.stdRiskTxtRef = this.stdRiskTxtList[0].value
              }
            } else {
              this.mainForm.stdRiskTxtRef = this.resetForm.stdRiskTxtRef
            }
          } else {
            this.busClsAppID = undefined
            this.mainForm.updColl = undefined
            this.accumType = undefined
            this.riskAccumForm.blkLabel = undefined
            this.riskAccumForm.blkGrpLabel = undefined
            this.riskAccumForm.blkSGrp1Label = undefined
            this.riskAccumForm.blkSGrp2Label = undefined
            this.mainForm.indGrp = undefined
            this.perIndGrp = true
            this.doIndGrpDesc()
            this.mainForm.applyGST = undefined
          }
          if (this.busClsAppID === 'MI' || this.busClsAppID === 'TR') {
            this.hideInitLoading()
          }

          this.stdRiskTxtRefLoading = false
        } catch (err) {
          this.stdRiskTxtRefLoading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getBusClsParam'))
      }
    },
    doRiskTextDialog() {
      this.riskTextDialog = true
      this.riskTextForm.riskUppTxt = this.resetRiskTextForm.riskUppTxt
      this.riskTextForm.riskLwrTxt = this.resetRiskTextForm.riskLwrTxt
    },
    doSaveRiskTxt() {
      this.riskTextForm.polNo = this.mainForm.polNo
      this.riskTextForm.renCnt = this.mainForm.renCnt
      this.riskTextForm.endtCnt = this.mainForm.endtCnt
      this.riskTextForm.riskGrpNo = this.mainForm.riskGrpNo
      this.riskTextForm.riskNo = this.mainForm.riskNo
      this.riskTextForm.busCls = this.mainForm.busCls

      this.policy.loading = true
      this.doRiskTxtSave().then(success => {
        if (success) {
          this.resetRiskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt
          this.resetRiskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt
          this.$toast.success('Risk Text Successfully Saved')
        } else {
          this.$toast.error('Risk Text Save Failed')
        }
        this.policy.loading = false
      })
    },
    async doRiskTxtSave() {
      try {
        // this.$store.commit(POLICY_RISK, this.mainForm)
        if (this.riskTextForm.riskUppTxt === undefined) {
          this.riskTextForm.riskUppTxt = ''
        }
        if (this.riskTextForm.riskLwrTxt === undefined) {
          this.riskTextForm.riskLwrTxt = ''
        }
        const result = await this.$store.dispatch('savePolRisk', this.riskTextForm)
        if (result) {
          return true
        } else {
          return false
        }
      } catch (err) {
        return false
      }
    },
    doLoadPreset() {
      this.defTextSelected = []
      if (this.tabMenu === 0) {
        this.scnRefNo = '20'
        this.defTextSelected = this.selectedRiskUppTxt
        this.defaulText = this.itemsRiskUppTxt
      } else if (this.tabMenu === 1) {
        this.scnRefNo = '21'
        this.defTextSelected = this.selectedRiskLwrTxt
        this.defaulText = this.itemsRiskLwrTxt
      }
    },
    doSelectDefText() {
      if (this.tabMenu === 0) {
        if (this.riskTextForm.riskUppTxt === undefined) {
          this.riskTextForm.riskUppTxt = ''
        }
        if (this.riskTextForm.riskUppTxt.length > 0 && this.riskTextForm.riskUppTxt.charAt(this.riskTextForm.riskUppTxt.length - 1) !== '\n') {
          this.riskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt + '\n'
        }
        for (var a = 0; a < this.defTextSelected.length; a++) {
          this.riskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt + this.defTextSelected[a].defTxt + '\n'
        }
      }
      if (this.tabMenu === 1) {
        if (this.riskTextForm.riskLwrTxt === undefined) {
          this.riskTextForm.riskLwrTxt = ''
        }
        if (this.riskTextForm.riskLwrTxt.length > 0 && this.riskTextForm.riskLwrTxt.charAt(this.riskTextForm.riskLwrTxt.length - 1) !== '\n') {
          this.riskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt + '\n'
        }
        for (var b = 0; b < this.defTextSelected.length; b++) {
          this.riskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt + this.defTextSelected[b].defTxt + '\n'
        }
      }
      this.loadPreSetDialog = false
    },
    async getTermReasonList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.termReasonList
      if (apiUrl) {
        const result = await this.$http.get(apiUrl)
        const { data } = result
        if (data) {
          data.data.map(termReason => {
            const termReasonList = {
              value: termReason.termCode,
              label: termReason.termCode + ' - ' + termReason.termReason
            }
            this.termReasons.push(termReasonList)
          })
        } else {
          this.termReasons = []
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getTermReasonList'))
      }
    },
    async getInjuredTypeList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.injuredPersonList
      if (apiUrl) {
        const result = await this.$http.get(apiUrl)
        const { data } = result
        if (data) {
          data.data.map(injuredType => {
            const injuredTypeList = {
              value: injuredType.injuredType,
              label: injuredType.injuredType + ' - ' + injuredType.injuredTypeDesc
            }
            this.injuredTypes.push(injuredTypeList)
          })
        } else {
          this.injuredTypes = []
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getInjuredTypeList'))
      }
    },
    async getRiskTextList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.riskTextList
      if (apiUrl) {
        try {
          const expand = {
            busCls: this.mainForm.busCls
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          if (result.data.riskUppTxtList) {
            const { data } = result.data.riskUppTxtList
            if (data) {
              this.itemsRiskUppTxt = []
              this.selectedRiskUppTxt = []
              data.map(upperText => {
                const upperTextList = {
                  txtRef: upperText.txtRef,
                  txtTitle: upperText.txtTitle,
                  defTxt: upperText.defTxt
                }
                this.itemsRiskUppTxt.push(upperTextList)
                if (upperText.txtDef === true) {
                  this.selectedRiskUppTxt.push(upperTextList)
                }
              })
            } else {
              this.itemsRiskUppTxt = []
            }
          }

          if (result.data.riskLwrTxtList) {
            const { data } = result.data.riskLwrTxtList
            if (data) {
              this.itemsRiskLwrTxt = []
              this.selectedRiskLwrTxt = []
              data.map(lowerText => {
                const lowerTextList = {
                  txtRef: lowerText.txtRef,
                  txtTitle: lowerText.txtTitle,
                  defTxt: lowerText.defTxt
                }
                this.itemsRiskLwrTxt.push(lowerTextList)
                if (lowerText.txtDef === true) {
                  this.selectedRiskLwrTxt.push(lowerTextList)
                }
              })
            } else {
              this.itemsRiskLwrTxt = []
            }
          }
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getRiskTextList'))
      }
    },
    async doEmpyeeDet() {
      this.resetMessages()
      this.empyeeDetForm.polNo = this.mainForm.polNo
      this.empyeeDetForm.renCnt = this.mainForm.renCnt
      this.empyeeDetForm.endtCnt = this.mainForm.endtCnt
      this.empyeeDetForm.riskGrpNo = this.mainForm.riskGrpNo
      this.empyeeDetForm.riskNo = this.mainForm.riskNo

      this.$validator.validateAll('empyeeDetForm').then(result1 => {
        if (result1) {
          this.doValidateEmpyeeDet().then(result2 => {
            if (result2) {
              this.policy.loading = true
              this.doEmpyeeDetSave().then(success => {
                if (success) {
                  this.$toast.success("Employee's Company Details Successfully Saved")
                  this.empyeeDetDialog = false
                  this.getPolEmpyeeDet(this.mainForm)
                } else {
                  this.$toast.error(this.failedMessage('employee details'))
                }
                this.policy.loading = false
              })
            } else {
              this.policy.loading = false
            }
          })
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doValidateEmpyeeDet() {
      let status = true
      if (!this.acceptEmpyeeCoUEN) {
        if (!this.doWarnEmpyeeCoUEN()) {
          status = false
        }
      }
      if (!this.acceptEmpyeeCoName) {
        if (!this.doWarnEmpyeeCoName()) {
          status = false
        }
      }
      if (!this.acceptNoEmpyeeInsd) {
        if (!this.doWarnNoEmpyeeInsd()) {
          status = false
        }
      }
      return status
    },
    async doEmpyeeDetSave() {
      try {
        const result = await this.$store.dispatch('savePolEmpyeeDet', this.empyeeDetForm)
        return result
      } catch (err) {
        return false
      }
    },
    async getPolEmpyeeDet(risk) {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.getPolEmpyeeDet
      if (apiUrl) {
        this.policy.loading = true
        try {
          let riskGrpNo = this.getFirstRiskList[0].riskGrpNo.replace(/^0+/, '')
          let riskNo = risk.riskNo.replace(/^0+/, '')
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: riskGrpNo,
            riskNo: riskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result.data
          if (data) {
            this.empyeeDetForm.empyeeCoName = data.polEmpyeeDet.empyeeCoName
            this.empyeeDetForm.empyeeCoName2 = data.polEmpyeeDet.empyeeCoName2
            this.empyeeDetForm.noEmpyeeInsd = data.polEmpyeeDet.noEmpyeeInsd
            this.empyeeDetForm.empyeeCoUEN = data.polEmpyeeDet.empyeeCoUEN
            this.empyeeDetForm.suspendDate = data.polEmpyeeDet.suspendDate
            this.empyeeDetForm.cancelDate = data.polEmpyeeDet.cancelDate
            this.empyeeDetForm.dnldDate = data.polEmpyeeDet.dnldDate
            this.empyeeDetForm.polNo = data.polEmpyeeDet.polNo
            this.empyeeDetForm.renCnt = data.polEmpyeeDet.renCnt
            this.empyeeDetForm.endtCnt = data.polEmpyeeDet.endtCnt
            this.empyeeDetForm.riskGrpNo = data.polEmpyeeDet.riskGrpNo.toString().padStart(2, '0')
            this.empyeeDetForm.riskNo = data.polEmpyeeDet.riskNo.toString().padStart(3, '0')
            this.empyeeDetForm.termCode = data.polEmpyeeDet.termCode // OS-1887
            this.empyeeDetForm.injuredType = data.polEmpyeeDet.injuredType
          } else {
            this.doResetEmpyeeDet(risk)
            this.empyeeDetForm.polNo = this.polMaster.polNo
            this.empyeeDetForm.renCnt = this.polMaster.renCnt
            this.empyeeDetForm.endtCnt = this.polMaster.endtCnt
            this.empyeeDetForm.riskGrpNo = riskGrpNo.toString().padStart(2, '0')
            this.empyeeDetForm.riskNo = riskNo.toString().padStart(3, '0')
          }
          this.policy.loading = false
        } catch (err) {
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      }
    },
    async doRiskClauseSave() {
      this.riskClseForm.polNo = this.mainForm.polNo
      this.riskClseForm.renCnt = this.mainForm.renCnt
      this.riskClseForm.endtCnt = this.mainForm.endtCnt
      this.riskClseForm.riskGrpNo = this.mainForm.riskGrpNo
      this.riskClseForm.riskNo = this.mainForm.riskNo
      this.riskClseForm.clauses = []
      for (var i = 0; i < this.riskClse.items.length; i++) {
        this.riskClseForm.clauses.push(this.riskClse.items[i].clauseNo)
      }
      try {
        this.policy.loading = true
        const saveResult = await this.$store.dispatch('saveRiskClse', this.riskClseForm)
        if (saveResult) {
          this.$toast.success('Risk Clause has been Saved')
          this.policy.loading = false
        } else {
          this.$toast.error('Risk Clause Save Failed')
          this.policy.loading = false
        }
      } catch (err) {
        this.policy.loading = false
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    doRiskClauseDelete() {
      for (var i = 0; i < this.riskClse.selected.length; i++) {
        for (var ii = 0; ii < this.riskClse.items.length; ii++) {
          if (this.riskClse.selected[i].clauseNo === this.riskClse.items[ii].clauseNo) {
            this.$delete(this.riskClse.items, ii)
          }
        }
      }
      this.clause.selected = this.riskClse.items
      this.loadDelRiskClseDialog = false
    },
    doResetRiskClse() {
      this.riskClse.selected = []
      this.riskClse.items = []
    },
    doResetRiskClseSeletedColumn() {
      this.riskClse.selected = []
    },
    doSelectClause() {
      this.clause.selected.map(select => {
        const clauseList = {
          clauseNo: select.clauseNo,
          clauseTitle: select.clauseTitle,
          clauseTxt: select.clauseTxt,
          clausePremWar: select.clausePremWar
        }
        this.clause.selectedClause.push(clauseList)
      })
      if (this.clause.selectedClause.length > 0 && this.riskClse.items.length > 0) {
        for (var i = 0; i < this.clause.selectedClause.length; i++) {
          for (var ii = 0; ii < this.riskClse.items.length; ii++) {
            if (this.clause.selectedClause[i].clauseNo === this.riskClse.items[ii].clauseNo) {
              this.$delete(this.clause.selectedClause, i)
            }
          }
        }
      }
      if (this.clause.selectedClause.length > 0) {
        for (var iii = 0; iii < this.clause.selectedClause.length; iii++) {
          this.riskClse.items.push(this.clause.selectedClause[iii])
        }
      }
      this.clause.selectedClause = []
    },
    async getClauseList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.clauseList
      if (apiUrl) {
        const expand = {
          busCls: this.mainForm.busCls
        }
        const result = await this.$http.get(expandURI(apiUrl, expand))
        const { data } = result
        if (data) {
          data.data.map(clause => {
            const clauseList = {
              clauseNo: clause.clseNo,
              clauseTitle: clause.clseTitle,
              clauseTxt: clause.clseTxt,
              clausePremWar: clause.isPremWar
            }
            this.clause.items.push(clauseList)
            this.clause.displayItems.push(clauseList)
            if (clause.defClse === true) {
              this.clause.selected.push(clauseList)
            }
          })
        } else {
          this.clause.items = []
          this.clause.displayItems = []
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getClauseList'))
      }
    },
    async doEmpyeeDetDialog() {
      this.empyeeDetDialog = true
    },
    doRiskClauseList() {
      this.riskClauseDialog = true
      this.getRiskClauseList()
    },
    async getRiskClauseList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.riskClauseList
      if (apiUrl) {
        try {
          this.riskClse.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.riskClse.items = []
            data.data.map(clause => {
              const clauseList = {
                clauseNo: clause.clseNo,
                clauseTitle: clause.clseTitle,
                clauseTxt: clause.clseTxt
              }
              this.riskClse.items.push(clauseList)
            })
            this.riskClse.loading = false
          } else {
            this.riskClse.items = []
            this.riskClse.loading = false
          }
          if (this.riskClse.items.length > 0) {
            this.clause.selected = this.riskClse.items
          }
        } catch (err) {
          this.riskClse.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getRiskClauseList'))
      }
    },
    async doPolRiskStatsList() {
      this.riskStatsDialog = true
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.riskStatsList
      if (apiUrl) {
        try {
          this.polRiskStats.items = []
          this.polRiskStats.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            busCls: this.mainForm.busCls
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(statistic => {
              const statisticList = {
                statsType: statistic.statsType,
                statsTypeDesc: statistic.statsTypeDesc,
                statsVal: statistic.statsValue,
                statsValDesc: statistic.statsValueDesc
              }
              this.polRiskStats.items.push(statisticList)
            })
            this.polRiskStats.loading = false
          } else {
            this.polRiskStats.items = []
            this.polRiskStats.loading = false
          }
        } catch (err) {
          this.polRiskStats.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('doPolRiskStatsList'))
      }
    },
    doShowStatValue(data) {
      this.getStatsValue(data.statsType)
      this.statsValDialog = true
    },
    async doRiskStatsSave() {
      this.polRiskStatsForm.polNo = this.mainForm.polNo
      this.polRiskStatsForm.renCnt = this.mainForm.renCnt
      this.polRiskStatsForm.endtCnt = this.mainForm.endtCnt
      this.polRiskStatsForm.riskGrpNo = this.mainForm.riskGrpNo
      this.polRiskStatsForm.riskNo = this.mainForm.riskNo

      this.polRiskStatsForm.statsType = []
      this.polRiskStatsForm.statsVal = []
      for (var i = 0; i < this.polRiskStats.items.length; i++) {
        this.polRiskStatsForm.statsType.push(this.polRiskStats.items[i].statsType)
        this.polRiskStatsForm.statsVal.push(this.polRiskStats.items[i].statsVal)
      }
      try {
        this.policy.loading = true
        const saveResult = await this.$store.dispatch('savePolRiskStats', this.polRiskStatsForm)
        if (saveResult) {
          const valStatus = await this.valRiskStatsVal()
          if (valStatus) {
            this.$toast.success('Policy Risk Statistics has been Saved')
            this.policy.loading = false
          } else {
            this.policy.loading = false
          }
        } else {
          this.$toast.error('Policy Risk Statistics Save Failed')
          this.policy.loading = false
        }
      } catch (err) {
        this.policy.loading = false
        const { data } = err.response
        if (data) {
          this.message(data)
        }
      }
    },
    doSelectedStatsValue(data) {
      for (var i = 0; i < this.polRiskStats.items.length; i++) {
        if (this.polRiskStats.items[i].statsType === data.statsType) {
          this.polRiskStats.items[i].statsVal = data.statsVal
          this.polRiskStats.items[i].statsValDesc = data.statsValDesc
        }
      }
      this.statsValDialog = false
    },
    async getStatsValue(statsType) {
      this.statsVal.items = []
      const apiUrl = this.$store.state.underwriting.policyHeaderSettings.menu.statsValList
      if (apiUrl) {
        try {
          this.statsVal.loading = true
          const expand = {
            statsType: statsType
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            data.data.map(statsValue => {
              const statsValueList = {
                statsVal: statsValue.statsVal,
                statsValDesc: statsValue.statsValDesc,
                statsType: statsType
              }
              this.statsVal.items.push(statsValueList)
            })
            this.statsVal.loading = false
          } else {
            this.statsVal.items = []
            this.statsVal.loading = false
          }
        } catch (err) {
          this.statsVal.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getStatsValue'))
      }
    },
    doAddRisk() {
      this.addRisk().then(() => {
        this.doBusCls()
        this.doInitSubComponent()
      })
    },
    doResetEmpyeeDet(risk) {
      this.resetMessages()
      this.empyeeDetForm.polNo = this.polMaster.polNo
      this.empyeeDetForm.renCnt = this.polMaster.renCnt
      this.empyeeDetForm.endtCnt = this.polMaster.endtCnt
      this.empyeeDetForm.riskGrpNo = risk.riskGrpNo
      this.empyeeDetForm.riskNo = risk.riskNo
      this.empyeeDetForm.empyeeCoUEN = ''
      this.empyeeDetForm.dnldDate = ''
      this.empyeeDetForm.empyeeCoName = ''
      this.empyeeDetForm.empyeeCoName2 = ''
      this.empyeeDetForm.cancelDate = ''
      this.empyeeDetForm.suspendDate = ''
      this.empyeeDetForm.noEmpyeeInsd = 0
      this.empyeeDetForm.termCode = ''
    },
    async addRisk() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.addRisk
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.selectedRiskGrp
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.riskList = []
            data.data.map(risk => {
              this.doResetEmpyeeDet(risk)
              const convertRisk = {
                riskGrpNoRiskNo: risk.riskGrpNo.toString().padStart(2, '0') + '/' + risk.riskNo.toString().padStart(3, '0'),
                riskGrpNo: risk.riskGrpNo.toString().padStart(2, '0'),
                riskNo: risk.riskNo.toString().padStart(3, '0'),
                busCls: risk.busCls,
                busClsDesc: risk.busClsDesc,
                siCurr: risk.siCurr,
                siExRate: risk.siExRate.toFixed(7),
                comDate: risk.comDate,
                comDateFormat: risk.comDate != null ? formatDisplayDate(risk.comDate) : undefined,
                expDate: risk.expDate,
                expDateFormat: risk.expDate != null ? formatDisplayDate(risk.expDate) : undefined,
                delRisk: formatYesNo(risk.delRisk),
                indGrp: risk.indGrp,
                stdRiskTxtRef: risk.stdRiskTxtRef,
                riskUppTxt: risk.riskUppTxt,
                riskLwrTxt: risk.riskLwrTxt,
                canFindPkgPlan: risk.canFindPkgPlan,
                newRiskNo: risk.newRiskNo,
                applyGST: risk.applyGST
              }
              this.riskList.push(convertRisk)
            })

            this.mainForm.riskGrpNo = this.getNewRisk[0].riskGrpNo
            this.mainForm.riskNo = this.getNewRisk[0].riskNo
            if (this.getNewRisk[0].delRisk === 'YES') {
              this.mainForm.delRisk = true
            } else {
              this.mainForm.delRisk = false
            }
            this.mainForm.busCls = this.getNewRisk[0].busCls
            this.mainForm.comDate = this.getNewRisk[0].comDate
            this.mainForm.expDate = this.getNewRisk[0].expDate
            this.mainForm.siCurr = this.getNewRisk[0].siCurr
            this.mainForm.siExRate = this.getNewRisk[0].siExRate
            this.mainForm.indGrp = this.getNewRisk[0].indGrp
            this.doIndGrpDesc()
            this.mainForm.stdRiskTxtRef = this.getNewRisk[0].stdRiskTxtRef
            this.mainForm.applyGST = this.getNewRisk[0].applyGST

            this.riskTextForm.riskUppTxt = this.getNewRisk[0].riskUppTxt
            this.riskTextForm.riskLwrTxt = this.getNewRisk[0].riskLwrTxt

            this.resetForm.riskGrpNo = this.mainForm.riskGrpNo
            this.resetForm.riskNo = this.mainForm.riskNo
            this.resetForm.delRisk = this.mainForm.delRisk
            this.resetForm.busCls = this.mainForm.busCls
            this.resetForm.comDate = this.mainForm.comDate
            this.resetForm.expDate = this.mainForm.expDate
            this.resetForm.siCurr = this.mainForm.siCurr
            this.resetForm.siExRate = this.mainForm.siExRate
            this.resetForm.indGrp = this.mainForm.indGrp
            this.resetForm.stdRiskTxtRef = this.mainForm.stdRiskTxtRef
            this.resetForm.applyGST = this.mainForm.applyGST

            this.resetRiskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt
            this.resetRiskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt

            this.policy.loading = false
          } else {
            this.riskList = []
            this.policy.loading = false
          }
          this.$toast.success('New Risk Added')
        } catch (err) {
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('addRisk'))
      }
    },
    doPromptRemoveRisk(data) {
      this.selectedRisk = data
      this.deleteTxt = 'Are you sure you want to delete risk no. ' + data.riskGrpNoRiskNo + '?'
      this.promptRemoveRisk = true
    },
    // AR: OS-644 20-Jan-2021
    doPromptUndoDelRisk(data) {
      this.selectedItem = data
      this.undoDeleteTxt = 'Confirm to undo delete risk no. ' + data.riskGrpNoRiskNo + '?'
      this.promptUndoRemoveRisk = true
    },
    async doUndoDelRisk() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.undoDelRisk
      if (apiUrl) {
        try {
          this.loading = true
          this.policy.loading = true
          this.policyRiskForm.polNo = this.polMaster.polNo
          this.policyRiskForm.renCnt = this.polMaster.renCnt
          this.policyRiskForm.endtCnt = this.polMaster.endtCnt
          this.policyRiskForm.riskGrpNo = this.selectedItem.riskGrpNo
          this.policyRiskForm.riskNo = this.selectedItem.riskNo
          let result = await this.$http.post(apiUrl, this.policyRiskForm)
          if (result) {
            this.$toast.success('Policy Risk successfully updated.')
            this.doInit()
            this.policy.initLoading = false
          } else {
            this.$toast.error('An error encountered unable to undo delete risk.')
          }
          this.policy.loading = false
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
            this.fieldMessage(data)
          }
          this.policy.loading = false
        }
      } else {
        this.$toast.error('An error encountered unable to undo delete risk.')
      }
    },
    async doRemoveRisk() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.delRisk
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.selectedRisk.riskGrpNo,
            riskNo: this.selectedRisk.riskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          const { data } = result
          if (data) {
            this.riskList = []
            data.data.map(risk => {
              const convertRisk = {
                riskGrpNoRiskNo: risk.riskGrpNo.toString().padStart(2, '0') + '/' + risk.riskNo.toString().padStart(3, '0'),
                riskGrpNo: risk.riskGrpNo.toString().padStart(2, '0'),
                riskNo: risk.riskNo.toString().padStart(3, '0'),
                busCls: risk.busCls,
                busClsDesc: risk.busClsDesc,
                siCurr: risk.siCurr,
                siExRate: risk.siExRate.toFixed(7),
                comDate: risk.comDate,
                comDateFormat: risk.comDate != null ? formatDisplayDate(risk.comDate) : undefined,
                expDate: risk.expDate,
                expDateFormat: risk.expDate != null ? formatDisplayDate(risk.expDate) : undefined,
                delRisk: formatYesNo(risk.delRisk),
                indGrp: risk.indGrp,
                stdRiskTxtRef: risk.stdRiskTxtRef,
                riskUppTxt: risk.riskUppTxt,
                riskLwrTxt: risk.riskLwrTxt,
                canFindPkgPlan: risk.canFindPkgPlan,
                newRiskNo: risk.newRiskNo,
                applyGST: risk.applyGST
              }
              this.riskList.push(convertRisk)
            })

            this.mainForm.riskGrpNo = this.getFirstRiskList[0].riskGrpNo
            this.mainForm.riskNo = this.getFirstRiskList[0].riskNo
            if (this.getFirstRiskList[0].delRisk === 'YES') {
              this.mainForm.delRisk = true
            } else {
              this.mainForm.delRisk = false
            }
            this.mainForm.busCls = this.getFirstRiskList[0].busCls
            this.mainForm.comDate = this.getFirstRiskList[0].comDate
            this.mainForm.expDate = this.getFirstRiskList[0].expDate
            this.mainForm.siCurr = this.getFirstRiskList[0].siCurr
            this.mainForm.siExRate = this.getFirstRiskList[0].siExRate
            this.mainForm.indGrp = this.getFirstRiskList[0].indGrp
            this.doIndGrpDesc()
            this.mainForm.stdRiskTxtRef = this.getFirstRiskList[0].stdRiskTxtRef
            this.mainForm.applyGST = this.getFirstRiskList[0].applyGST

            this.riskTextForm.riskUppTxt = this.getFirstRiskList[0].riskUppTxt
            this.riskTextForm.riskLwrTxt = this.getFirstRiskList[0].riskLwrTxt

            this.doBusCls()
            this.doInitSubComponent()

            this.resetForm.riskGrpNo = this.mainForm.riskGrpNo
            this.resetForm.riskNo = this.mainForm.riskNo
            this.resetForm.delRisk = this.mainForm.delRisk
            this.resetForm.busCls = this.mainForm.busCls
            this.resetForm.comDate = this.mainForm.comDate
            this.resetForm.expDate = this.mainForm.expDate
            this.resetForm.siCurr = this.mainForm.siCurr
            this.resetForm.siExRate = this.mainForm.siExRate
            this.resetForm.indGrp = this.mainForm.indGrp
            this.resetForm.stdRiskTxtRef = this.mainForm.stdRiskTxtRef
            this.resetForm.applyGST = this.mainForm.applyGST

            this.resetRiskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt
            this.resetRiskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt

            this.$toast.success('Policy risk record successfully deleted.')
            this.policy.loading = false
          } else {
            this.riskList = []
            this.policy.loading = false
          }
        } catch (err) {
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('doRemoveRisk'))
      }
    },
    async doSelectRisk(data) {
      this.showExpandRisk = true
      this.mainForm.riskGrpNo = data.riskGrpNo
      this.mainForm.riskNo = data.riskNo
      if (data.delRisk === 'YES') {
        this.mainForm.delRisk = true
      } else {
        this.mainForm.delRisk = false
      }
      this.resetForm.busCls = data.busCls
      this.resetForm.stdRiskTxtRef = data.stdRiskTxtRef
      this.mainForm.busCls = data.busCls
      this.mainForm.comDate = data.comDate
      this.mainForm.expDate = data.expDate
      this.mainForm.siCurr = data.siCurr
      this.mainForm.siExRate = data.siExRate
      this.mainForm.indGrp = data.indGrp
      this.doIndGrpDesc()
      this.mainForm.stdRiskTxtRef = data.stdRiskTxtRef
      this.mainForm.applyGST = data.applyGST

      this.riskTextForm.riskUppTxt = data.riskUppTxt
      this.riskTextForm.riskLwrTxt = data.riskLwrTxt
      this.resetRiskTextForm.riskUppTxt = this.riskTextForm.riskUppTxt
      this.resetRiskTextForm.riskLwrTxt = this.riskTextForm.riskLwrTxt

      await this.doBusCls()
      await this.doInitSubComponent()
      this.getPolEmpyeeDet(data)
      this.copymainForm = clone(this.mainForm)
    },
    doExit() {
      this.$router.push({ name: 'policyListing' })
    },
    hideInitLoading() {
      this.policy.initLoading = false
    },
    doReset() {
      this.doInit()
      this.doInitSubComponent()
    },
    doSave() {
      this.resetMessages()
      this.expandRiskDetails()
      this.doValidateAllWarn().then(result4 => {
        if (result4) {
          this.$validator.validateAll().then(result3 => {
            if (result3) {
              if (this.busClsAppID === 'BD') {
                if (this.$refs.polBondRisk) {
                  this.resetMessages()
                  this.$refs.polBondRisk.$validator.validateAll().then(result2 => {
                    if (this.$refs.polBondRisk.workerForm.wrkmanNo !== undefined) {
                      this.$refs.polBondRisk.$validator.validateAll('workerForm').then(result => {
                        if (result) {
                          this.policy.loading = true
                          this.$refs.polBondRisk.workerForm.polNo = this.$refs.polBondRisk.mainForm.polNo
                          this.$refs.polBondRisk.workerForm.renCnt = this.$refs.polBondRisk.mainForm.renCnt
                          this.$refs.polBondRisk.workerForm.endtCnt = this.$refs.polBondRisk.mainForm.endtCnt
                          this.$refs.polBondRisk.workerForm.riskGrpNo = this.$refs.polBondRisk.mainForm.riskGrpNo
                          this.$refs.polBondRisk.workerForm.riskNo = this.$refs.polBondRisk.mainForm.riskNo
                          this.$refs.polBondRisk.doPolMiscWrkmanSave().then(success => {
                            if (success) {
                              this.$refs.polBondRisk.initWorkerForm()
                              this.$refs.polBondRisk.displayWorkerList()
                              this.$refs.polBondRisk.doPolMiscEmpytSave().then(success2 => {
                                if (success2) {
                                  if (this.saveMRisk) {
                                    this.doMiscRiskSave().then(success3 => {
                                      if (success3) {
                                        this.getRiskList()
                                        this.$toast.success('Risk Details Successfully Saved')
                                      } else {
                                        this.$toast.error(this.failedMessage('miscellaneous risk'))
                                        this.getIndGrp()
                                      }
                                    })
                                  } else {
                                    this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                                    this.getIndGrp()
                                  }
                                } else {
                                  this.$toast.error(this.failedMessage('policy bond risk'))
                                }
                              })
                            } else {
                              this.$toast.error(this.failedMessage('worker'))
                            }
                            this.policy.loading = false
                          })
                        } else {
                          this.policy.loading = false
                          this.promptFieldCheck()
                        }
                      })
                    } else if (result2) {
                      this.policy.loading = true
                      this.$refs.polBondRisk.doPolMiscEmpytSave().then(success => {
                        if (success) {
                          if (this.saveMRisk) {
                            this.doMiscRiskSave().then(success3 => {
                              if (success3) {
                                this.getRiskList()
                                this.$toast.success('Risk Details Successfully Saved')
                              } else {
                                this.$toast.error(this.failedMessage('miscellaneous risk'))
                                this.getIndGrp()
                              }
                            })
                          } else {
                            this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                            this.getIndGrp()
                          }
                        } else {
                          this.$toast.error(this.failedMessage('policy bond risk'))
                        }
                        this.policy.loading = false
                      })
                    } else {
                      this.policy.loading = false
                      this.promptFieldCheck()
                    }
                  })
                }
              } else if (this.busClsAppID === 'FI') {
                if (this.$refs.polFireRisk) {
                  this.resetMessages()
                  this.$refs.polFireRisk.$validator.validateAll().then(result => {
                    if (result) {
                      this.policy.loading = true
                      this.$refs.polFireRisk.doPolFireRiskSave().then(success => {
                        if (success) {
                          this.$refs.polFireRisk.getPolFireRisk()
                          if (this.saveMRisk) {
                            this.doMiscRiskSave().then(success3 => {
                              if (success3) {
                                this.getRiskList()
                                this.$toast.success('Risk Details Successfully Saved')
                              } else {
                                this.$toast.error(this.failedMessage('miscellaneous risk'))
                                this.getIndGrp()
                              }
                            })
                          } else {
                            this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                            this.getIndGrp()
                          }
                        } else {
                          this.$toast.error(this.failedMessage('policy fire risk'))
                        }
                        this.policy.loading = false
                      })
                    } else {
                      this.policy.loading = false
                      this.promptFieldCheck()
                    }
                  })
                }
              } else if (this.busClsAppID === 'MO') {
                if (this.$refs.polMtrRisk) {
                  this.resetMessages()
                  if (this.$refs.polMtrRisk.namedDrvMand === true && this.$refs.polMtrRisk.drivers.list.length === 0) {
                    this.$toast.error('Please click on Named Drivers and enter details of named drivers. (20196)')
                  } else {
                    this.$refs.polMtrRisk.$validator.validateAll().then(result2 => {
                      if (this.$refs.polMtrRisk.driverForm.seqNo !== undefined) {
                        this.$refs.polMtrRisk.$validator.validateAll('driverForm').then(result => {
                          if (result) {
                            this.policy.loading = true
                            this.$refs.polMtrRisk.driverForm.polNo = this.$refs.polMtrRisk.mainForm.polNo
                            this.$refs.polMtrRisk.driverForm.renCnt = this.$refs.polMtrRisk.mainForm.renCnt
                            this.$refs.polMtrRisk.driverForm.endtCnt = this.$refs.polMtrRisk.mainForm.endtCnt
                            this.$refs.polMtrRisk.driverForm.riskGrpNo = this.$refs.polMtrRisk.mainForm.riskGrpNo
                            this.$refs.polMtrRisk.driverForm.riskNo = this.$refs.polMtrRisk.mainForm.riskNo
                            this.$refs.polMtrRisk.doPolMtrDrvSave().then(success => {
                              if (success) {
                                this.$refs.polMtrRisk.initDriverForm()
                                this.$refs.polMtrRisk.displayDriverList()
                                this.$refs.polMtrRisk.doPolMtrRiskSave().then(success2 => {
                                  if (success2) {
                                    if (this.saveMRisk) {
                                      this.doMiscRiskSave().then(success3 => {
                                        if (success3) {
                                          this.getRiskList()
                                          this.$toast.success('Risk Details Successfully Saved')
                                        } else {
                                          this.$toast.error(this.failedMessage('miscellaneous risk'))
                                          this.getIndGrp()
                                        }
                                      })
                                    } else {
                                      this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                                      this.getIndGrp()
                                    }
                                  } else {
                                    this.$toast.error(this.failedMessage('policy motor risk'))
                                  }
                                })
                              } else {
                                this.$toast.error('Named Driver Save Failed')
                              }
                              this.policy.loading = false
                            })
                          } else {
                            this.policy.loading = false
                            this.promptFieldCheck()
                          }
                        })
                      } else if (result2) {
                        this.policy.loading = true
                        this.$refs.polMtrRisk.doPolMtrRiskSave().then(success => {
                          if (success) {
                            if (this.saveMRisk) {
                              this.doMiscRiskSave().then(success3 => {
                                if (success3) {
                                  this.getRiskList()
                                  this.$toast.success('Risk Details Successfully Saved')
                                } else {
                                  this.$toast.error(this.failedMessage('miscellaneous risk'))
                                  this.getIndGrp()
                                }
                              })
                            } else {
                              this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                              this.getIndGrp()
                            }
                          } else {
                            this.$toast.error(this.failedMessage('policy motor risk'))
                          }
                          this.policy.loading = false
                        })
                      } else {
                        this.policy.loading = false
                        this.promptFieldCheck()
                      }
                    })
                  }
                }
              } else if (this.busClsAppID === 'HU') {
                if (this.$refs.polHullRisk) {
                  this.resetMessages()
                  this.$refs.polHullRisk.$validator.validateAll().then(result => {
                    if (result) {
                      this.policy.loading = true
                      this.$refs.polHullRisk.doPolHullRiskSave().then(success => {
                        if (success) {
                          if (this.saveMRisk) {
                            this.doMiscRiskSave().then(success3 => {
                              if (success3) {
                                this.getRiskList()
                                this.$toast.success('Policy risk record successfully saved')
                              } else {
                                this.$toast.error(this.failedMessage('miscellaneous risk'))
                                this.getIndGrp()
                              }
                            })
                          } else {
                            this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                            this.getIndGrp()
                          }
                        } else {
                          this.$toast.error('Policy Hull Risk Save Failed')
                        }
                        this.policy.loading = false
                      })
                    } else {
                      this.policy.loading = false
                      this.promptFieldCheck()
                    }
                  })
                }
              } else if (this.busClsAppID === 'CA') {
                if (this.$refs.polCargoRisk) {
                  this.resetMessages()
                  this.$refs.polCargoRisk.$validator.validateAll().then(result => {
                    if (result) {
                      this.policy.loading = true
                      this.$refs.polCargoRisk.doPolCargoRiskSave().then(success => {
                        if (success) {
                          if (this.saveMRisk) {
                            this.doMiscRiskSave().then(success3 => {
                              if (success3) {
                                this.getRiskList()
                                this.$toast.success('Policy risk record successfully saved')
                              } else {
                                this.$toast.error(this.failedMessage('miscellaneous risk'))
                                this.getIndGrp()
                              }
                            })
                          } else {
                            this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                            this.getIndGrp()
                          }
                        } else {
                          const saveErrMsg = this.$store.state.underwriting.policy.polCargoRisk.saveError.messages[0]
                          if (saveErrMsg) {
                            this.$toast.error(saveErrMsg)
                          } else {
                            this.$toast.error(this.failedMessage('policy cargo risk'))
                          }
                        }
                        this.policy.loading = false
                      })
                    } else {
                      this.policy.loading = false
                      this.promptFieldCheck()
                    }
                  })
                }
              } else if (this.busClsAppID === 'MI' || this.busClsAppID === 'TR') {
                this.doMiscRiskSave().then(miSuccess => {
                  if (miSuccess) {
                    this.getRiskList()
                    this.$toast.success('Risk Details Successfully Saved')
                  } else {
                    this.$toast.error(this.failedMessage('miscellaneous risk'))
                    this.getIndGrp()
                  }
                })
              }
            } else {
              this.policy.loading = false
              this.promptFieldCheck()
            }
          })
        }
      })
    },
    doProceedBondNo() {
      this.acceptBondNo = true
      if (!this.$refs.polBondRisk.allowBondNo && !this.$refs.polBondRisk.validBondNo) {
        this.$refs.polBondRisk.allowBondNo = true
      }
    },
    async isExistedPolBondRisk() {
      let isExisted = false
      let status = false
      if (this.$refs.polBondRisk) {
        isExisted = true
      }
      if (isExisted) {
        await this.$refs.polBondRisk.doValBondNo2().then(result5 => {
          if (result5 || this.acceptBondNo) {
            this.warnBondNo = false
            this.warnBondNoMsg = undefined
            if (!this.$refs.polBondRisk.validBondNo && this.$refs.polBondRisk.allowBondNo) {
              this.acceptBondNo = true
            } else {
              this.acceptBondNo = this.$refs.polBondRisk.validBondNo
            }
            if (
              (this.acceptBondNo && result5) ||
              (result5 && this.$refs.polBondRisk.validBondNo) ||
              (!result5 && this.$refs.polBondRisk.allowBondNo && this.acceptBondNo)
            ) {
              status = true
            }
          } else if (!result5 && !this.acceptBondNo) {
            this.warnBondNo = true
            this.warnBondNoMsg = this.$refs.polBondRisk.warnBondNoMsg
            if (this.$refs.polBondRisk.allowBondNo && !this.$refs.polBondRisk.validBondNo) {
              status = true
            } else {
              status = false
            }
          }
        })
        return status
      } else {
        this.acceptBondNo = true
        return true
      }
    },
    expandRiskDetails() {
      if (this.busClsAppID === 'BD') {
        if (this.$refs.polBondRisk) {
          this.$refs.polBondRisk.detail_panel = [0]
        }
      } else if (this.busClsAppID === 'FI') {
        if (this.$refs.polFireRisk) {
          this.$refs.polFireRisk.detail_panel = [0]
        }
      } else if (this.busClsAppID === 'MO') {
        if (this.$refs.polMtrRisk) {
          this.$refs.polMtrRisk.detail_panel = [0]
        }
      } else if (this.busClsAppID === 'HU') {
        if (this.$refs.polHullRisk) {
          this.$refs.polHullRisk.detail_panel = [0]
        }
      } else if (this.busClsAppID === 'CA') {
        if (this.$refs.polCargoRisk) {
          this.$refs.polCargoRisk.detail_panel = [0]
        }
      }
    },
    doProceed() {
      this.resetMessages()
      this.expandRiskDetails()
      this.doValidateAllWarn().then(result4 => {
        if (result4) {
          this.$validator.validateAll().then(result3 => {
            if (result3) {
              this.policy.loading = true
              this.valPolEmpyeeDet().then(empyeeDetObj => {
                this.empyeeDetStat.isValid = empyeeDetObj.isValid
                this.empyeeDetStat.isBlank = empyeeDetObj.isBlank
                if (Object.keys(empyeeDetObj).length > 0) {
                  this.doWarnEmptyEmpyeeDet()
                }
                if (this.acceptEmpyeeDet || this.empyeeDetExist) {
                  this.isExistedPolBondRisk().then(result5 => {
                    if (result5) {
                      if (this.acceptBondNo) {
                        this.valRiskAccum().then(successRiskAccumVal => {
                          if (successRiskAccumVal) {
                            this.valAllRiskStatsVal().then(successVal => {
                              if (successVal) {
                                if (this.busClsAppID === 'BD') {
                                  if (this.$refs.polBondRisk) {
                                    this.resetMessages()
                                    this.$refs.polBondRisk.$validator.validateAll().then(result2 => {
                                      if (this.$refs.polBondRisk.workerForm.wrkmanNo !== undefined) {
                                        this.$refs.polBondRisk.$validator.validateAll('workerForm').then(result => {
                                          if (result) {
                                            this.policy.loading = true
                                            this.$refs.polBondRisk.workerForm.polNo = this.$refs.polBondRisk.mainForm.polNo
                                            this.$refs.polBondRisk.workerForm.renCnt = this.$refs.polBondRisk.mainForm.renCnt
                                            this.$refs.polBondRisk.workerForm.endtCnt = this.$refs.polBondRisk.mainForm.endtCnt
                                            this.$refs.polBondRisk.workerForm.riskGrpNo = this.$refs.polBondRisk.mainForm.riskGrpNo
                                            this.$refs.polBondRisk.workerForm.riskNo = this.$refs.polBondRisk.mainForm.riskNo
                                            this.$refs.polBondRisk.doPolMiscWrkmanSave().then(success => {
                                              if (success) {
                                                this.$refs.polBondRisk.initWorkerForm()
                                                this.$refs.polBondRisk.displayWorkerList()
                                                this.$refs.polBondRisk.doPolMiscEmpytSave().then(success2 => {
                                                  if (success2) {
                                                    if (this.saveMRisk) {
                                                      this.doMiscRiskSave().then(success3 => {
                                                        if (success3) {
                                                          this.$store.commit(POLICY_RISK_TAB, 2)
                                                          this.$router.push({ name: 'policyCoverage' })
                                                        } else {
                                                          this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                          this.policy.loading = false
                                                          this.getIndGrp()
                                                        }
                                                      })
                                                    } else {
                                                      this.policy.loading = false
                                                      this.$toast.error(this.failedMessage('50002-Not found on xmm526 Industry Group Code file.'))
                                                      this.getIndGrp()
                                                    }
                                                  } else {
                                                    this.$toast.error(this.failedMessage('policy bond risk'))
                                                    this.policy.loading = false
                                                  }
                                                })
                                              } else {
                                                this.$toast.error(this.failedMessage('worker'))
                                                this.policy.loading = false
                                              }
                                            })
                                          } else {
                                            this.policy.loading = false
                                            this.promptFieldCheck()
                                          }
                                        })
                                      } else if (result2) {
                                        this.policy.loading = true
                                        this.$refs.polBondRisk.doPolMiscEmpytSave().then(success => {
                                          if (success) {
                                            if (this.saveMRisk) {
                                              this.doMiscRiskSave().then(success3 => {
                                                if (success3) {
                                                  this.$store.commit(POLICY_RISK_TAB, 2)
                                                  this.$router.push({ name: 'policyCoverage' })
                                                } else {
                                                  this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                  this.policy.loading = false
                                                  this.getIndGrp()
                                                }
                                              })
                                            } else {
                                              this.policy.loading = false
                                              this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                              this.getIndGrp()
                                            }
                                          } else {
                                            this.$toast.error(this.failedMessage('policy bond risk'))
                                            this.policy.loading = false
                                          }
                                        })
                                      } else {
                                        this.policy.loading = false
                                        this.promptFieldCheck()
                                      }
                                    })
                                  }
                                } else if (this.busClsAppID === 'FI') {
                                  if (this.$refs.polFireRisk) {
                                    this.resetMessages()
                                    this.$refs.polFireRisk.$validator.validateAll().then(result => {
                                      if (result) {
                                        this.policy.loading = true
                                        this.$refs.polFireRisk.doPolFireRiskSave().then(success => {
                                          if (success) {
                                            this.$refs.polFireRisk.getPolFireRisk()
                                            if (this.saveMRisk) {
                                              this.doMiscRiskSave().then(success3 => {
                                                if (success3) {
                                                  this.$store.commit(POLICY_RISK_TAB, 2)
                                                  this.$router.push({ name: 'policyCoverage' })
                                                } else {
                                                  this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                  this.policy.loading = false
                                                  this.getIndGrp()
                                                }
                                              })
                                            } else {
                                              this.policy.loading = false
                                              this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                              this.getIndGrp()
                                            }
                                          } else {
                                            const saveErrMsg = this.$store.state.underwriting.policy.polFireRisk.saveError.messages[0]
                                            if (saveErrMsg) {
                                              this.$toast.error(saveErrMsg)
                                              this.policy.loading = false
                                            } else {
                                              this.$toast.error(this.failedMessage('policy fire risk'))
                                              this.policy.loading = false
                                            }
                                          }
                                        })
                                      } else {
                                        this.policy.loading = false
                                        this.promptFieldCheck()
                                      }
                                    })
                                  }
                                } else if (this.busClsAppID === 'MO') {
                                  if (this.$refs.polMtrRisk) {
                                    this.resetMessages()
                                    if (this.$refs.polMtrRisk.namedDrvMand === true && this.$refs.polMtrRisk.drivers.list.length === 0) {
                                      this.$toast.error('Please click on Named Drivers and enter details of named drivers. (20196)')
                                      this.policy.loading = false
                                    } else {
                                      this.$refs.polMtrRisk.$validator.validateAll().then(result2 => {
                                        if (this.$refs.polMtrRisk.driverForm.seqNo !== undefined) {
                                          this.$refs.polMtrRisk.$validator.validateAll('driverForm').then(result => {
                                            if (result) {
                                              this.policy.loading = true
                                              this.$refs.polMtrRisk.driverForm.polNo = this.$refs.polMtrRisk.mainForm.polNo
                                              this.$refs.polMtrRisk.driverForm.renCnt = this.$refs.polMtrRisk.mainForm.renCnt
                                              this.$refs.polMtrRisk.driverForm.endtCnt = this.$refs.polMtrRisk.mainForm.endtCnt
                                              this.$refs.polMtrRisk.driverForm.riskGrpNo = this.$refs.polMtrRisk.mainForm.riskGrpNo
                                              this.$refs.polMtrRisk.driverForm.riskNo = this.$refs.polMtrRisk.mainForm.riskNo
                                              this.$refs.polMtrRisk.doPolMtrDrvSave().then(success => {
                                                if (success) {
                                                  this.$refs.polMtrRisk.initDriverForm()
                                                  this.$refs.polMtrRisk.displayDriverList()
                                                  this.$refs.polMtrRisk.doPolMtrRiskSave().then(success2 => {
                                                    if (success2) {
                                                      if (this.saveMRisk) {
                                                        this.doMiscRiskSave().then(success3 => {
                                                          if (success3) {
                                                            this.$store.commit(POLICY_RISK_TAB, 2)
                                                            this.$router.push({ name: 'policyCoverage' })
                                                          } else {
                                                            this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                            this.policy.loading = false
                                                            this.getIndGrp()
                                                          }
                                                        })
                                                      } else {
                                                        this.policy.loading = false
                                                        this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                                        this.getIndGrp()
                                                      }
                                                    } else {
                                                      this.$toast.error(this.failedMessage('policy motor risk'))
                                                      this.policy.loading = false
                                                    }
                                                  })
                                                } else {
                                                  this.$toast.error('Named Driver Save Failed')
                                                  this.policy.loading = false
                                                }
                                              })
                                            } else {
                                              this.policy.loading = false
                                              this.promptFieldCheck()
                                            }
                                          })
                                        } else if (result2) {
                                          this.policy.loading = true
                                          this.$refs.polMtrRisk.doPolMtrRiskSave().then(success => {
                                            if (success) {
                                              if (this.saveMRisk) {
                                                this.doMiscRiskSave().then(success3 => {
                                                  if (success3) {
                                                    this.$store.commit(POLICY_RISK_TAB, 2)
                                                    this.$router.push({ name: 'policyCoverage' })
                                                  } else {
                                                    this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                    this.policy.loading = false
                                                    this.getIndGrp()
                                                  }
                                                })
                                              } else {
                                                this.policy.loading = false
                                                this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                                this.getIndGrp()
                                              }
                                            } else {
                                              this.$toast.error(this.failedMessage('policy motor risk'))
                                              this.policy.loading = false
                                            }
                                          })
                                        } else {
                                          this.policy.loading = false
                                          this.promptFieldCheck()
                                        }
                                      })
                                    }
                                  }
                                } else if (this.busClsAppID === 'HU') {
                                  if (this.$refs.polHullRisk) {
                                    this.resetMessages()
                                    this.$refs.polHullRisk.$validator.validateAll().then(result => {
                                      if (result) {
                                        this.policy.loading = true
                                        this.$refs.polHullRisk.doPolHullRiskSave().then(success => {
                                          if (success) {
                                            if (this.saveMRisk) {
                                              this.doMiscRiskSave().then(success3 => {
                                                if (success3) {
                                                  this.$store.commit(POLICY_RISK_TAB, 2)
                                                  this.$router.push({ name: 'policyCoverage' })
                                                } else {
                                                  this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                  this.policy.loading = false
                                                  this.getIndGrp()
                                                }
                                              })
                                            } else {
                                              this.policy.loading = false
                                              this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                              this.getIndGrp()
                                            }
                                          } else {
                                            this.$toast.error(this.failedMessage('policy hull risk'))
                                            this.policy.loading = false
                                          }
                                        })
                                      } else {
                                        this.policy.loading = false
                                        this.promptFieldCheck()
                                      }
                                    })
                                  }
                                } else if (this.busClsAppID === 'CA') {
                                  if (this.$refs.polCargoRisk) {
                                    this.resetMessages()
                                    this.$refs.polCargoRisk.$validator.validateAll().then(result => {
                                      if (result) {
                                        this.policy.loading = true
                                        this.$refs.polCargoRisk.doPolCargoRiskSave().then(success => {
                                          if (success) {
                                            if (this.saveMRisk) {
                                              this.doMiscRiskSave().then(success3 => {
                                                if (success3) {
                                                  this.$store.commit(POLICY_RISK_TAB, 2)
                                                  this.$router.push({ name: 'policyCoverage' })
                                                } else {
                                                  this.$toast.error(this.failedMessage('miscellaneous risk'))
                                                  this.policy.loading = false
                                                  this.getIndGrp()
                                                }
                                              })
                                            } else {
                                              this.policy.loading = false
                                              this.$toast.error(this.failedMessage('50002 - Not found on Industry Group Code file.'))
                                              this.getIndGrp()
                                            }
                                          } else {
                                            const saveErrMsg = this.$store.state.underwriting.policy.polCargoRisk.saveError.messages[0]
                                            if (saveErrMsg) {
                                              this.$toast.error(saveErrMsg)
                                              this.policy.loading = false
                                            } else {
                                              this.$toast.error(this.failedMessage('policy cargo risk'))
                                              this.policy.loading = false
                                            }
                                          }
                                        })
                                      } else {
                                        this.policy.loading = false
                                        this.promptFieldCheck()
                                      }
                                    })
                                  }
                                } else if (this.busClsAppID === 'MI' || this.busClsAppID === 'TR') {
                                  this.resetMessages()
                                  this.doMiscRiskSave().then(success => {
                                    if (success) {
                                      this.$store.commit(POLICY_RISK_TAB, 2)
                                      this.$router.push({ name: 'policyCoverage' })
                                    } else {
                                      this.$toast.error(this.failedMessage('miscellaneous risk'))
                                      this.policy.loading = false
                                      this.getIndGrp()
                                    }
                                  })
                                }
                              } else {
                                this.policy.loading = false
                                this.$parent.$parent.setTabs(1)
                              }
                            })
                          } else {
                            this.policy.loading = false
                            this.$parent.$parent.setTabs(1)
                          }
                        })
                      } else {
                        this.policy.loading = false
                      }
                    } else {
                      this.policy.loading = false
                    }
                  })
                } else {
                  this.policy.loading = false
                }
              })
            } else {
              this.policy.loading = false
              this.promptFieldCheck()
            }
          })
        }
      })
    },
    // AR: Added 22-May-2020
    processTab(tabIndex, isValid) {
      if (isValid) {
        this.doProceed()
        // this.$parent.$parent.changeTab(tabIndex)
      } else {
        this.$parent.$parent.changeTab(this.$parent.$parent.headers)
      }
    },
    // AR: Added 22-May-2020
    doValidateRisk(tabIndex) {
      this.resetMessages()
      this.$validator.validateAll().then(result3 => {
        if (result3) {
          if (this.busClsAppID === 'BD') {
            if (this.$refs.polBondRisk) {
              this.resetMessages()
              this.$refs.polBondRisk.$validator.validateAll().then(result2 => {
                if (this.$refs.polBondRisk.workerForm.wrkmanNo !== undefined) {
                  this.$refs.polBondRisk.$validator.validateAll('workerForm').then(result => {
                    if (result) {
                      this.processTab(tabIndex, true)
                    } else {
                      this.processTab(tabIndex, false)
                      this.promptFieldCheck()
                    }
                  })
                } else {
                  this.processTab(tabIndex, false)
                  this.promptFieldCheck()
                }
              })
            }
          } else if (this.busClsAppID === 'FI') {
            if (this.$refs.polFireRisk) {
              this.resetMessages()
              this.$refs.polFireRisk.$validator.validateAll().then(result => {
                if (result) {
                  this.processTab(tabIndex, true)
                } else {
                  this.processTab(tabIndex, false)
                  this.promptFieldCheck()
                }
              })
            }
          } else if (this.busClsAppID === 'MO') {
            if (this.$refs.polMtrRisk) {
              this.resetMessages()
              this.$refs.polMtrRisk.$validator.validateAll().then(result2 => {
                if (result2) {
                  if (this.$refs.polMtrRisk.namedDrvMand === true && this.$refs.polMtrRisk.drivers.list.length === 0) {
                    this.$toast.error('Please click on Named Drivers and enter details of named drivers. (20196)')
                    this.processTab(tabIndex, false)
                  } else {
                    this.processTab(tabIndex, true)
                  }
                } else {
                  this.processTab(tabIndex, false)
                  this.promptFieldCheck()
                }
              })
            }
          } else if (this.busClsAppID === 'HU') {
            if (this.$refs.polHullRisk) {
              this.resetMessages()
              this.$refs.polHullRisk.$validator.validateAll().then(result => {
                if (result) {
                  this.processTab(tabIndex, true)
                } else {
                  this.processTab(tabIndex, false)
                  this.promptFieldCheck()
                }
              })
            }
          } else if (this.busClsAppID === 'CA') {
            if (this.$refs.polCargoRisk) {
              this.resetMessages()
              this.$refs.polCargoRisk.$validator.validateAll().then(result => {
                if (result) {
                  this.processTab(tabIndex, true)
                } else {
                  this.processTab(tabIndex, false)
                  this.promptFieldCheck()
                }
              })
            }
          }
        } else {
          this.processTab(tabIndex, false)
          this.promptFieldCheck()
        }
      })
    },
    doRiskAccum() {
      this.riskAccumForm.polNo = undefined
      this.riskAccumForm.renCnt = undefined
      this.riskAccumForm.endtCnt = undefined
      this.riskAccumForm.riskGrpNo = undefined
      this.riskAccumForm.riskNo = undefined
      this.riskAccumForm.seqNo = undefined
      this.riskAccumForm.cfmRisk = undefined
      // this.riskAccumForm.blkGrpLabel = undefined
      this.riskAccumForm.blkGrp = undefined
      // this.riskAccumForm.blkSGrp1Label = undefined
      this.riskAccumForm.blkSGrp1 = undefined
      // this.riskAccumForm.blkSGrp2Label = undefined
      this.riskAccumForm.blkSGrp2 = undefined
      // this.riskAccumForm.blkLabel = undefined
      this.riskAccumForm.blk = undefined
      this.riskAccumForm.floatSilim = undefined
      this.riskAccumForm.floatPrem = undefined
      this.riskAccumForm.blkGrpLblS = undefined
      this.riskAccumForm.blkGrpLblL = undefined
      this.riskAccumForm.createdName = undefined
      this.riskAccumForm.modifiedName = undefined
      this.riskAccumForm.createdBy = undefined
      this.riskAccumForm.createdDate = undefined
      this.riskAccumForm.modifiedBy = undefined
      this.riskAccumForm.modifiedDate = undefined
      this.riskAccumForm.blksGrp1LblS = undefined
      this.riskAccumForm.blksGrp1LblL = undefined
      this.riskAccumForm.blksGrp2LblS = undefined
      this.riskAccumForm.blksGrp2LblL = undefined
      this.riskAccumForm.blksLblS = undefined
      this.riskAccumForm.blksLblL = undefined
      if (this.accumType !== undefined && this.accumType !== '' && this.accumType !== null) {
        this.riskAccumDialog = true
        this.getRiskAccumList()
      } else {
        this.$toast.warning('Accumulation type is not yet setup.')
      }
    },
    async getRiskAccumList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.riskAccumList
      if (apiUrl) {
        try {
          this.riskAccum.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))
          this.displayRiskBlockList(result).then(() => {
            this.riskAccum.loading = false
            if (this.riskAccum.items.length > 0) {
              this.doSetRiskBlock(this.riskAccum.items[0])
            }
          })
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('getriskaccumList'))
      }
    },
    async displayRiskBlockList(result) {
      const { data } = result
      if (data) {
        this.riskAccum.items = []
        data.data.map(polRiskBlk => {
          const polRiskBlkList = {
            polNo: polRiskBlk.polNo,
            renCnt: polRiskBlk.renCnt,
            endtCnt: polRiskBlk.endtCnt,
            riskGrpNo: polRiskBlk.riskGrpNo,
            riskNo: polRiskBlk.riskNo,
            seqNo: polRiskBlk.seqNo,
            accmType: polRiskBlk.accmType,
            blkGrp: polRiskBlk.blkGrp,
            blkSGrp1: polRiskBlk.blkSgrp1,
            blkSGrp2: polRiskBlk.blkSgrp2,
            blk: polRiskBlk.blk,
            floatSilim: formatPrice(polRiskBlk.floatSilim),
            floatPrem: formatPrice(polRiskBlk.floatPrem),
            effDate: polRiskBlk.effDate,
            cfmRisk: polRiskBlk.cfmRisk,
            cfmRiskYN: formatYesNo(polRiskBlk.cfmRisk),
            createdName: polRiskBlk.createdName,
            modifiedName: polRiskBlk.modifiedName,
            createdBy: polRiskBlk.createdBy,
            createdDate: formatDisplayDateTime2(polRiskBlk.createdDate),
            modifiedBy: polRiskBlk.modifiedBy,
            modifiedDate: formatDisplayDateTime2(polRiskBlk.modifiedDate),
            newRec: polRiskBlk.newRec,
            blkGrpDesc: polRiskBlk.blkGrpDesc,
            blkSgrp1Desc: polRiskBlk.blkSgrp1Desc,
            blkSgrp2Desc: polRiskBlk.blkSgrp2Desc,
            blkDesc: polRiskBlk.blkDesc
          }
          this.riskAccum.items.push(polRiskBlkList)
        })
      } else {
        this.riskAccum.items = []
      }
      return true
    },
    async doAddRiskBlock() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.addRiskBlock
      if (apiUrl) {
        try {
          this.riskAccum.loading = true
          const expand = {
            polNo: this.mainForm.polNo,
            renCnt: this.mainForm.renCnt,
            endtCnt: this.mainForm.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayRiskBlockList(result).then(() => {
            this.riskAccum.loading = false
            this.doSetRiskBlock(this.getNewRiskBlock[0])
          })
          this.$toast.success('New risk block sucessfully added.')
        } catch (err) {
          this.riskAccum.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('doAddRiskBlock'))
      }
    },
    doSetRiskBlock(data) {
      this.valBlkGrp = 'required'
      this.valBlkSGrp1 = 'required'
      this.valBlkSGrp2 = 'required'
      this.valBlk = 'required'
      this.riskAccumForm.seqNo = data.seqNo
      this.riskAccumForm.cfmRisk = data.cfmRisk
      this.riskAccumForm.blkGrp = data.blkGrp
      this.riskAccumForm.blkSGrp1 = data.blkSGrp1
      this.riskAccumForm.blkSGrp2 = data.blkSGrp2
      this.riskAccumForm.blk = data.blk
      this.riskAccumForm.floatSilim = data.floatSilim
      this.riskAccumForm.floatPrem = data.floatPrem
      this.riskAccumForm.createdName = data.createdName
      this.riskAccumForm.modifiedName = data.modifiedName
      this.riskAccumForm.createdBy = data.createdBy
      this.riskAccumForm.createdDate = data.createdDate
      this.riskAccumForm.modifiedBy = data.modifiedBy
      this.riskAccumForm.modifiedDate = data.modifiedDate
      this.riskAccumForm.blkGrpLblS = data.blkGrp
      this.riskAccumForm.blkGrpLblL = data.blkGrpDesc
      this.riskAccumForm.blksGrp1LblS = data.blkSGrp1
      this.riskAccumForm.blksGrp1LblL = data.blkSgrp1Desc
      this.riskAccumForm.blksGrp2LblS = data.blkSGrp2
      this.riskAccumForm.blksGrp2LblL = data.blkSgrp2Desc
      this.riskAccumForm.blksLblS = data.blk
      this.riskAccumForm.blksLblL = data.blkDesc
    },
    doShowBlkGrp() {
      if (this.riskAccumForm.seqNo !== undefined && this.riskAccumForm.blkGrpLabel !== undefined && this.riskAccumForm.blkGrpLabel !== null) {
        this.riskAccumBlkGrpDialog = true
        this.riskAccumForm.accumType = this.accumType
        this.riskAccumBlkGrpInit(this.riskAccumForm.accumType)
      }
    },
    doShowBlkSGrp1() {
      if (
        this.riskAccumForm.seqNo !== undefined &&
        this.riskAccumForm.blkGrpLabel !== undefined &&
        this.riskAccumForm.blkGrpLabel !== null &&
        this.riskAccumForm.blkGrpLblS !== undefined &&
        this.riskAccumForm.blkSGrp1Label !== undefined &&
        this.riskAccumForm.blkSGrp1Label !== null
      ) {
        this.riskAccumBlkSubGrp1Dialog = true
        this.riskAccumForm.accumType = this.accumType
        // this.riskAccumForm.blkGrpLblS = this.riskAccumForm.blkGrpLblS
        this.riskAccumBlkSubGrp1Init(this.riskAccumForm)
      }
    },
    doShowBlkSGrp2() {
      if (
        this.riskAccumForm.seqNo !== undefined &&
        this.riskAccumForm.blkGrpLabel !== undefined &&
        this.riskAccumForm.blkGrpLabel !== null &&
        this.riskAccumForm.blkGrpLblS !== undefined &&
        (this.riskAccumForm.blkSGrp1Label === undefined ||
          this.riskAccumForm.blkSGrp1Label === null ||
          (this.riskAccumForm.blkSGrp1Label !== undefined &&
            this.riskAccumForm.blkSGrp1Label !== null &&
            this.riskAccumForm.blksGrp1LblS !== undefined)) &&
        this.riskAccumForm.blkSGrp2Label !== undefined &&
        this.riskAccumForm.blkSGrp2Label !== null
      ) {
        this.riskAccumBlkSubGrp2Dialog = true
        this.riskAccumForm.accumType = this.accumType
        // this.riskAccumForm.blkGrpLblS = this.riskAccumForm.blkGrpLblS
        // this.riskAccumForm.blksGrp1LblS = this.riskAccumForm.blksGrp1LblS
        this.riskAccumBlkSubGrp2Init(this.riskAccumForm)
      }
    },
    doShowBlk() {
      if (
        this.riskAccumForm.seqNo !== undefined &&
        this.riskAccumForm.blkGrpLabel !== undefined &&
        this.riskAccumForm.blkGrpLabel !== null &&
        this.riskAccumForm.blkGrpLblS !== undefined &&
        (this.riskAccumForm.blkSGrp1Label === undefined ||
          this.riskAccumForm.blkSGrp1Label === null ||
          (this.riskAccumForm.blkSGrp1Label !== undefined &&
            this.riskAccumForm.blkSGrp1Label !== null &&
            this.riskAccumForm.blksGrp1LblS !== undefined)) &&
        (this.riskAccumForm.blkSGrp2Label === undefined ||
          this.riskAccumForm.blkSGrp2Label === null ||
          (this.riskAccumForm.blkSGrp2Label !== undefined &&
            this.riskAccumForm.blkSGrp2Label !== null &&
            this.riskAccumForm.blksGrp2LblS !== undefined)) &&
        this.riskAccumForm.blkLabel !== undefined &&
        this.riskAccumForm.blkLabel !== null
      ) {
        this.riskAccumBlkDialog = true
        this.riskAccumForm.accumType = this.accumType
        // this.riskAccumForm.blkGrpLblS = this.riskAccumForm.blkGrpLblS
        // this.riskAccumForm.blksGrp1LblS = this.riskAccumForm.blksGrp1LblS
        // this.riskAccumForm.blksGrp2LblS = this.riskAccumForm.blksGrp2LblS

        if (this.riskAccumForm.effDate === undefined) {
          if (this.polMaster.endtCnt === 0) {
            this.riskAccumForm.effDate =
              this.polMaster.comDate !== undefined && this.polMaster.comDate !== null ? this.polMaster.comDate : this.polMaster.tranDate
          } else if (this.polMaster.endtCnt > 0) {
            this.riskAccumForm.effDate = this.polMaster.endtDate
          }
        }
        this.riskAccumBlkInit(this.riskAccumForm)
      }
    },
    doClose() {
      this.riskAccumBlkGrpDialog = false
      this.riskAccumBlkSubGrp1Dialog = false
      this.riskAccumBlkSubGrp2Dialog = false
      this.riskAccumBlkDialog = false
      this.perIndGrpHelpList = false
      this.perIndGrpList = false
      this.busClsHelpList = false
    },
    doPromptRemoveRiskBlock(data) {
      this.selectedRiskBlock = data
      this.deleteRiskBlock = 'Are you sure you want to delete risk block sequence no. ' + data.seqNo + '?'
      this.promptRemoveRiskBlock = true
    },
    async doRemoveRiskBlock() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.delRiskBlock
      if (apiUrl) {
        try {
          this.riskAccum.loading = true
          const expand = {
            polNo: this.selectedRiskBlock.polNo,
            renCnt: this.selectedRiskBlock.renCnt,
            endtCnt: this.selectedRiskBlock.endtCnt,
            riskGrpNo: this.selectedRiskBlock.riskGrpNo,
            riskNo: this.selectedRiskBlock.riskNo,
            seqNo: this.selectedRiskBlock.seqNo
          }

          const result = await this.$http.get(expandURI(apiUrl, expand))

          this.displayRiskBlockList(result).then(() => {
            this.riskAccum.loading = false
            if (this.riskAccum.items.length > 0) {
              this.doSetRiskBlock(this.riskAccum.items[0])
            } else {
              this.initRiskAccumForm()
            }
          })
          this.$toast.success('Policy risk block successfully deleted.')
        } catch (err) {
          this.riskAccum.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error(this.invalidAPIUrlMessage('doRemoveRiskBlock'))
      }
    },
    initRiskAccumForm() {
      this.riskAccumForm.seqNo = undefined
      this.riskAccumForm.cfmRisk = undefined
      // this.riskAccumForm.blkGrpLabel = undefined
      this.riskAccumForm.blkGrp = undefined
      // this.riskAccumForm.blkSGrp1Label = undefined
      this.riskAccumForm.blkSGrp1 = undefined
      // this.riskAccumForm.blkSGrp2Label = undefined
      this.riskAccumForm.blkSGrp2 = undefined
      // this.riskAccumForm.blkLabel = undefined
      this.riskAccumForm.blk = undefined
      this.riskAccumForm.floatSilim = undefined
      this.riskAccumForm.floatPrem = undefined
      this.riskAccumForm.blkGrpLblS = undefined
      this.riskAccumForm.blkGrpLblL = undefined
      this.riskAccumForm.createdName = undefined
      this.riskAccumForm.modifiedName = undefined
      this.riskAccumForm.createdBy = undefined
      this.riskAccumForm.createdDate = undefined
      this.riskAccumForm.modifiedBy = undefined
      this.riskAccumForm.modifiedDate = undefined
      this.riskAccumForm.blksGrp1LblS = undefined
      this.riskAccumForm.blksGrp1LblL = undefined
      this.riskAccumForm.blksGrp2LblS = undefined
      this.riskAccumForm.blksGrp2LblL = undefined
      this.riskAccumForm.blksLblS = undefined
      this.riskAccumForm.blksLblL = undefined
    },
    doSaveRiskAccum() {
      this.resetMessages()
      this.$validator.validateAll().then(result => {
        if (result) {
          this.policy.loading = true
          this.valSaveRiskAccum().then(result2 => {
            if (result2) {
              this.riskAccumForm.polNo = this.mainForm.polNo
              this.riskAccumForm.renCnt = this.mainForm.renCnt
              this.riskAccumForm.endtCnt = this.mainForm.endtCnt
              this.riskAccumForm.riskGrpNo = this.mainForm.riskGrpNo
              this.riskAccumForm.riskNo = this.mainForm.riskNo
              if (this.riskAccumForm.blkGrpLblS !== undefined && this.riskAccumForm.blkGrpLblS !== null) {
                this.riskAccumForm.blkGrp = this.riskAccumForm.blkGrpLblS
              } else {
                this.riskAccumForm.blkGrp = ''
              }

              if (this.riskAccumForm.blksGrp1LblS !== undefined && this.riskAccumForm.blksGrp1LblS !== null) {
                this.riskAccumForm.blkSGrp1 = this.riskAccumForm.blksGrp1LblS
              } else {
                this.riskAccumForm.blkSGrp1 = ''
              }

              if (this.riskAccumForm.blksGrp2LblS !== undefined && this.riskAccumForm.blksGrp2LblS !== null) {
                this.riskAccumForm.blkSGrp2 = this.riskAccumForm.blksGrp2LblS
              } else {
                this.riskAccumForm.blkSGrp2 = ''
              }

              if (this.riskAccumForm.blksLblS !== undefined && this.riskAccumForm.blksLblS !== null) {
                this.riskAccumForm.blk = this.riskAccumForm.blksLblS
              } else {
                this.riskAccumForm.blk = ''
              }
              this.riskAccumForm.floatSilim = removeFormatNumber(this.riskAccumForm.floatSilim)
              this.riskAccumForm.floatPrem = removeFormatNumber(this.riskAccumForm.floatPrem)

              this.doRiskBlockSave().then(success => {
                if (success) {
                  this.$toast.success('Risk Block Successfully Saved')
                  this.getRiskAccumList()
                } else {
                  this.$toast.error(this.failedMessage('risk block'))
                }
                this.policy.loading = false
              })
            } else {
              this.policy.loading = false
            }
          })
        } else {
          this.promptFieldCheck()
        }
      })
    },
    async doRiskBlockSave() {
      try {
        const result = await this.$store.dispatch('savePolRiskBlock', this.riskAccumForm)
        return result
      } catch (err) {
        return false
      }
    },
    async getIndGrp() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.industryList
      if (apiUrl) {
        this.$http
          .get(expandURI(apiUrl))
          .then(result => {
            const { data } = result.data.industryLists
            if (data) {
              if (this.mainForm.indGrp !== undefined || this.mainForm.indGrp !== '') {
                if (data.some(industry => industry.indGrp === this.mainForm.indGrp)) {
                  this.saveMRisk = true
                } else {
                  this.saveMRisk = false
                }
              }
              if (this.mainForm.indGrp === undefined || this.mainForm.indGrp === '') {
                this.saveMRisk = true
              }
            }
          })
          .catch(err => {
            const { data } = err.response
            if (data) {
              this.loading = false
              this.message(data)
              this.fieldMessage(data)
            }
          })
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url [getIndGrp].')
      }
    },
    getHelpList(fieldName) {
      if (fieldName === 'perIndGrp') {
        this.perIndGrpHelpList = true
        this.perIndGrpList = true
      } else if (fieldName === 'busCls') {
        this.busClsHelpList = true
      }
    },
    doAssignIndustryHelpList(industry) {
      this.mainForm.indGrp = industry.indGrp
      this.indGrpDesc = industry.indGrpDesc
      this.getIndGrp()
    },
    doAssignBusClsHelpList(busCls) {
      this.mainForm.busCls = busCls.busCls
      this.busClsAppID = busCls.busClsAppID
      this.doBusClsInit()
    },
    doSelectRiskBlock(data) {
      this.doSetRiskBlock(data)
    },
    successMessage() {
      return 'Policy record successfully saved.'
    },
    failedMessage(module) {
      if (module === '' || module === undefined) {
        return 'An unexpected error occured while saving policy record.'
      } else {
        return 'An unexpected error occured while saving ' + module + ' record.'
      }
    },
    invalidAPIUrlMessage(i) {
      return 'The request failed because it is trying to execute an invalid API url. [' + i + ']'
    },
    async doInitSubComponent() {
      if (this.busClsAppID === 'FI') {
        if (this.$refs.polFireRisk) {
          if (this.$store.state.underwriting.policyFireRiskSettings.init !== 1) {
            this.$store.dispatch('policyFireRiskInit').then(() => {
              this.$refs.polFireRisk.doInit()
            })
          } else {
            this.$refs.polFireRisk.doInit()
          }
        }
      } else if (this.busClsAppID === 'BD') {
        if (this.$refs.polBondRisk) {
          if (this.$store.state.underwriting.policyBondRiskSettings.init !== 1) {
            this.$store.dispatch('policyBondRiskInit').then(() => {
              this.$refs.polBondRisk.doInit()
            })
          } else {
            this.$refs.polBondRisk.doInit()
          }
        }
      } else if (this.busClsAppID === 'MO') {
        if (this.$refs.polMtrRisk) {
          if (this.$store.state.underwriting.policyMotorRiskSettings.init !== 1) {
            this.$store.dispatch('policyMotorRiskInit').then(() => {
              this.$refs.polMtrRisk.doInit()
            })
          } else {
            this.$refs.polMtrRisk.doInit()
          }
        }
      } else if (this.busClsAppID === 'HU') {
        if (this.$refs.polHullRisk) {
          if (this.$store.state.underwriting.policyHullRiskSettings.init !== 1) {
            this.$store.dispatch('policyHullRiskInit').then(() => {
              this.$refs.polHullRisk.doInit()
            })
          } else {
            this.$refs.polHullRisk.doInit()
          }
        }
      } else if (this.busClsAppID === 'CA') {
        if (this.$refs.polCargoRisk) {
          if (this.$store.state.underwriting.policyCargoRiskSettings.init !== 1) {
            this.$store.dispatch('policyCargoRiskInit').then(() => {
              this.$refs.polCargoRisk.doInit()
            })
          } else {
            this.$refs.polCargoRisk.doInit()
          }
        }
        // AR: Added 5-Sep-2020
      } else if (this.busClsAppID === 'MI' || this.busClsAppID === 'TR') {
        this.hideInitLoading()
      }
    },
    doIndGrpDesc() {
      if (this.getIndGrpDesc.length > 0) {
        this.indGrpDesc = this.getIndGrpDesc[0].label
      } else {
        this.indGrpDesc = ''
      }
    },
    async valPolEmpyeeDet() {
      if (this.wrkmanComp) {
        const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.valPolEmpyeeDet
        if (apiUrl) {
          try {
            let riskGrpNo = this.mainForm.riskGrpNo.replace(/^0+/, '')
            let riskNo = this.mainForm.riskNo.replace(/^0+/, '')
            const expand = {
              polNo: this.polMaster.polNo,
              renCnt: this.polMaster.renCnt,
              endtCnt: this.polMaster.endtCnt,
              riskGrpNo: riskGrpNo,
              riskNo: riskNo
            }
            const result = await this.$http.get(expandURI(apiUrl, expand))
            let { data } = result.data
            if (data) {
              return data
            }
            return {}
          } catch (err) {
            const { data } = err.response
            if (data) {
              this.message(data)
            }
            return {}
          }
        } else {
          this.errorMessage('The request failed because it is trying to execute an invalid API url. [valRiskStatsVal]')
        }
      } else {
        return { isBlank: false, isValid: true }
      }
    },
    async valRiskStatsVal() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.valRiskStatsVal
      if (apiUrl) {
        try {
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo
          }
          await this.$http.get(expandURI(apiUrl, expand))
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url. [valRiskStatsVal]')
      }
    },
    async valAllRiskStatsVal() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.valAllRiskStatsVal
      if (apiUrl) {
        try {
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          await this.$http.get(expandURI(apiUrl, expand))
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url. [valAllRiskStatsVal]')
      }
    },
    docloseRiskAccum() {
      // 2020-11-20 LYT OS-576
      this.riskAccumDialog = false
      // this.resetMessages()
      // this.$validator.validateAll().then(result => {
      //   if (result) {
      //     this.policy.loading = true
      //     this.valRiskAccumList().then(successRiskAccumVal => {
      //       if (successRiskAccumVal) {
      //         this.riskAccumDialog = false
      //         this.policy.loading = false
      //       } else {
      //         this.policy.loading = false
      //       }
      //     })
      //   } else {
      //     this.promptFieldCheck()
      //   }
      // })
    },
    async valRiskAccum() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.valRiskAccum
      if (apiUrl) {
        try {
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt,
            riskGrpNo: this.mainForm.riskGrpNo,
            riskNo: this.mainForm.riskNo,
            curBusCls: this.mainForm.busCls
          }
          await this.$http.get(expandURI(apiUrl, expand))
          return true
        } catch (err) {
          const { data } = err.response
          if (data) {
            this.message(data)
          }
          return false
        }
      } else {
        this.errorMessage('The request failed because it is trying to execute an invalid API url. [valRiskAccum]')
      }
    },
    async getShowBlkGrp() {
      this.valBlkGrp = 'required'
      this.riskAccumForm.blkGrpLblL = ''

      if (this.riskAccumForm.blkGrpLblS !== undefined && this.riskAccumForm.blkGrpLblS !== null && this.riskAccumForm.blkGrpLblS !== '') {
        this.riskAccumForm.accumType = this.accumType
        const blk = {
          blkGrp: this.riskAccumForm.blkGrpLblS
        }

        const status = await this.checkBlkGrp(blk)
        if (status === false) {
          this.valBlkGrp = 'required|valField:' + 'Not found in Block Group parameters (10120)'
        } else {
          this.valBlkGrp = 'required'
        }

        return status
      } else {
        return false
      }
    },
    async getShowBlkSGrp1() {
      this.valBlkSGrp1 = 'required'
      this.riskAccumForm.blksGrp1LblL = ''

      // if ((this.riskAccumForm.blkGrpLblS !== undefined && this.riskAccumForm.blkGrpLblS !== null && this.riskAccumForm.blkGrpLblS !== '') &&
      //   (this.riskAccumForm.blksGrp1LblS !== undefined && this.riskAccumForm.blksGrp1LblS !== null && this.riskAccumForm.blksGrp1LblS !== '')) {
      if (this.riskAccumForm.blksGrp1LblS !== undefined && this.riskAccumForm.blksGrp1LblS !== null && this.riskAccumForm.blksGrp1LblS !== '') {
        this.riskAccumForm.accumType = this.accumType
        const blk = {
          blkGrp: this.riskAccumForm.blkGrpLblS,
          blksGrp1: this.riskAccumForm.blksGrp1LblS
        }

        const status = await this.checkBlkSubGrp1(blk)
        if (status === false) {
          this.valBlkSGrp1 = 'required|valField:' + 'Not found in Block Sub Group 1 parameters (10121)'
        } else {
          this.valBlkSGrp1 = 'required'
        }
        return status
      } else {
        return false
      }
    },
    async getShowBlkSGrp2() {
      this.valBlkSGrp2 = 'required'
      this.riskAccumForm.blksGrp2LblL = ''

      // if ((this.riskAccumForm.blkGrpLblS !== undefined && this.riskAccumForm.blkGrpLblS !== null && this.riskAccumForm.blkGrpLblS !== '') &&
      //   (this.riskAccumForm.blksGrp1LblS !== undefined && this.riskAccumForm.blksGrp1LblS !== null && this.riskAccumForm.blksGrp1LblS !== '') &&
      if (this.riskAccumForm.blksGrp2LblS !== undefined && this.riskAccumForm.blksGrp2LblS !== null && this.riskAccumForm.blksGrp2LblS !== '') {
        this.riskAccumForm.accumType = this.accumType
        const blk = {
          blkGrp: this.riskAccumForm.blkGrpLblS,
          blksGrp1: this.riskAccumForm.blksGrp1LblS,
          blksGrp2: this.riskAccumForm.blksGrp2LblS
        }

        const status = await this.checkBlkSubGrp2(blk)
        if (status === false) {
          this.valBlkSGrp2 = 'required|valField:' + 'Not found in Block Sub Group 2 parameters (10121)'
        } else {
          this.valBlkSGrp2 = 'required'
        }
        return status
      } else {
        return false
      }
    },
    async getShowBlk() {
      this.valBlk = 'required'
      this.riskAccumForm.blksLblL = ''

      // if ((this.riskAccumForm.blkGrpLblS !== undefined && this.riskAccumForm.blkGrpLblS !== null && this.riskAccumForm.blkGrpLblS !== '') &&
      //   (this.riskAccumForm.blksGrp1LblS !== undefined && this.riskAccumForm.blksGrp1LblS !== null && this.riskAccumForm.blksGrp1LblS !== '') &&
      //   (this.riskAccumForm.blksGrp2LblS !== undefined && this.riskAccumForm.blksGrp2LblS !== null && this.riskAccumForm.blksGrp2LblS !== '') &&
      if (this.riskAccumForm.blksLblS !== undefined && this.riskAccumForm.blksLblS !== null && this.riskAccumForm.blksLblS !== '') {
        this.riskAccumForm.accumType = this.accumType

        if (this.riskAccumForm.effDate === undefined) {
          if (this.polMaster.endtCnt === 0) {
            this.riskAccumForm.effDate =
              this.polMaster.comDate !== undefined && this.polMaster.comDate !== null ? this.polMaster.comDate : this.polMaster.tranDate
          } else if (this.polMaster.endtCnt > 0) {
            this.riskAccumForm.effDate = this.polMaster.endtDate
          }
        }
        const blk = {
          blkGrp: this.riskAccumForm.blkGrpLblS,
          blksGrp1: this.riskAccumForm.blksGrp1LblS,
          blksGrp2: this.riskAccumForm.blksGrp2LblS,
          blk: this.riskAccumForm.blksLblS
        }

        const status = await this.checkBlk(blk)
        if (status === false) {
          this.valBlk = 'required|valField:' + 'No record found in Block Parameters. (10122)'
        } else {
          this.valBlk = 'required'
        }
        return status
      } else {
        return false
      }
    },
    async valSaveRiskAccum() {
      let status = true
      if (this.riskAccumForm.blkGrpLabel !== undefined && this.riskAccumForm.blkGrpLabel !== null && this.riskAccumForm.blkGrpLabel !== '') {
        status = await this.getShowBlkGrp()
      }

      if (
        status === true &&
        this.riskAccumForm.blkSGrp1Label !== undefined &&
        this.riskAccumForm.blkSGrp1Label !== null &&
        this.riskAccumForm.blkSGrp1Label !== ''
      ) {
        status = await this.getShowBlkSGrp1()
      }

      if (
        status === true &&
        this.riskAccumForm.blkSGrp2Label !== undefined &&
        this.riskAccumForm.blkSGrp2Label !== null &&
        this.riskAccumForm.blkSGrp2Label !== ''
      ) {
        status = await this.getShowBlkSGrp2()
      }

      if (
        status === true &&
        this.riskAccumForm.blkLabel !== undefined &&
        this.riskAccumForm.blkLabel !== null &&
        this.riskAccumForm.blkLabel !== ''
      ) {
        status = await this.getShowBlk()
      }

      return status
    },
    doPromptDelRiskClause() {
      // 2020-11-26 LYT SR5661
      let clauseNo = ''
      for (var i = 0; i < this.riskClse.selected.length; i++) {
        if (clauseNo !== '') {
          clauseNo = clauseNo + ','
        }
        clauseNo = clauseNo + this.riskClse.selected[i].clauseNo
      }
      this.labels.delRiskClseMsg = 'Are you sure you want to delete risk clauses? ' + clauseNo
      this.loadDelRiskClseDialog = true
    },
    showPromptAddRisk() {
      this.resetMessages()
      this.promptAddRisk = true
      this.mainForm.selectedRiskGrp = undefined
    },
    async getRiskGrpList() {
      const apiUrl = this.$store.state.underwriting.policyRiskSettings.menu.getRiskGrpNoList
      if (apiUrl) {
        try {
          this.policy.loading = true
          const expand = {
            polNo: this.polMaster.polNo,
            renCnt: this.polMaster.renCnt,
            endtCnt: this.polMaster.endtCnt
          }
          const result = await this.$http.get(expandURI(apiUrl, expand))

          const { data } = result
          if (data) {
            data.data.map(polRisk => {
              const tmpRiskGrpNos = {
                label: 'Risk Grp ' + polRisk.value.toString().padStart(2, '0'),
                value: polRisk.value
              }
              this.riskGrpNos.push(tmpRiskGrpNos)
            })
            this.policy.loading = false
          } else {
            this.policy.loading = false
          }
        } catch (err) {
          this.policy.loading = false
          const { data } = err.response
          if (data) {
            this.message(data)
          }
        }
      } else {
        this.$toast.error('The request failed because it is trying to execute an invalid API url.')
      }
    }
  },
  created() {
    const valField = {
      getMessage: (field, args) => {
        if (args === void 0) args = []
        var errMsg = args[0]
        return errMsg
      },
      validate: (value, args) => {
        if (value) {
          if (args === void 0) args = []
          var errMsg = args[0]
          if (errMsg) {
            return false
          } else return true
        } else return true
      }
    }
    this.$validator.extend('valField', valField)
  },
  beforeMount() {
    if (this.$store.state.underwriting.policyHeaderSettings.init !== 1) {
      this.$store.dispatch('policyHeaderInit')
    }
    if (this.$store.state.underwriting.policyRiskSettings.init !== 1) {
      this.$store.dispatch('policyRiskInit').then(() => {
        this.doInit()
      })
    } else {
      this.doInit()
    }
  },
  mixins: [TableConst.default, MessageMixin, riskAccumListMixin, riskAccumAjaxMixin]
}
</script>
